import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Linkify from 'react-linkify';
import { API, getAuthToken, removeAuthToken, updateAppBadge } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuItem } from '../components/ui/dropdown-menu';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Phone, Mail, Building2, ArrowLeft, MessageSquare, Search, Gift, Send, Users as UsersIcon, TrendingUp, UserCheck, UserX, CheckCircle2, XCircle, Clock, Briefcase, History, ArrowRight, Bell, MessageCircle, UserPlus, FileText, Linkedin, MapPin, Upload, X, Home, Building, Laptop, Facebook, Twitter, Share2, ExternalLink, Eye, GraduationCap, DollarSign, ChevronRight, LogOut, Archive, ArchiveRestore, Loader2, User, Settings, Menu, Plus, Check, Edit, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { RichTextEditor } from '../components/ui/rich-text-editor';
import ProfilePhotoUpload from '../components/ProfilePhotoUpload';
import JobsDashboard from '../components/dashboard/JobsDashboard';

// IST (Indian Standard Time) formatting utility - Kolkata/Chennai timezone
// Using 12-hour format with AM/PM
const formatIST = (dateString, formatStr = 'MMM dd, h:mm a') => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const options = { timeZone: 'Asia/Kolkata' };
    const istDate = new Date(date.toLocaleString('en-US', options));
    return format(istDate, formatStr);
  } catch (e) {
    return dateString;
  }
};
const formatISTTime = (dateString) => formatIST(dateString, 'h:mm a');
const formatISTDate = (dateString) => formatIST(dateString, 'MMM dd, yyyy');
const formatISTFull = (dateString) => formatIST(dateString, 'MMM dd, yyyy h:mm a');

// Format message content with clickable links, emails, and phone numbers
const formatMessageContent = (content) => {
  if (!content) return content;
  
  let formatted = content;
  
  // URL regex - matches http, https, www links
  const urlRegex = /(?<!href=["'])(?<!src=["'])(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;
  
  // Email regex
  const emailRegex = /(?<!mailto:)([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/gi;
  
  // Phone regex - matches various formats
  const phoneRegex = /(?<![\d-])(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}(?![\d-])/g;
  
  if (!/<a[^>]*>/.test(formatted)) {
    formatted = formatted.replace(urlRegex, (match) => {
      const href = match.startsWith('www.') ? `https://${match}` : match;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline break-all">${match}</a>`;
    });
    
    formatted = formatted.replace(emailRegex, (match) => {
      return `<a href="mailto:${match}" class="text-blue-600 hover:text-blue-800 underline">${match}</a>`;
    });
    
    formatted = formatted.replace(phoneRegex, (match) => {
      const cleanNumber = match.replace(/[\s()-]/g, '');
      return `<a href="tel:${cleanNumber}" class="text-blue-600 hover:text-blue-800 underline">${match}</a>`;
    });
  }
  
  return formatted;
};

// Safe HTML Renderer component for messages
const SafeHtmlRenderer = ({ html, className = '' }) => {
  if (!html) return null;
  return (
    <div 
      className={className}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

// My Performance Dashboard Component for Lead Specialists
const MyPerformanceDashboard = () => {
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchPerformance();
  }, []);
  
  const fetchPerformance = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API}/lead-specialist/my-performance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPerformanceData(response.data);
    } catch (error) {
      console.error('Failed to fetch performance data:', error);
      toast.error('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }
  
  if (!performanceData) {
    return (
      <div className="text-center py-8 text-slate-500">
        Failed to load performance data
      </div>
    );
  }
  
  const { stats, earnings, monthly, incentive_structure, recent_referrals, payment_history } = performanceData;
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="font-heading text-xl md:text-3xl font-bold tracking-tight mb-1 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
          My Performance
        </h2>
        <p className="text-sm text-muted-foreground">Track your referrals, placements, and earnings</p>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-lg rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">Total Referrals</p>
                <p className="text-2xl font-bold text-slate-800">{stats.total_referrals}</p>
                <p className="text-xs text-blue-600">{stats.recent_referrals} this month</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <UsersIcon className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-lg rounded-2xl bg-gradient-to-br from-emerald-50 to-green-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">Placements</p>
                <p className="text-2xl font-bold text-emerald-700">{stats.placements}</p>
                <p className="text-xs text-emerald-600">Successfully hired</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-lg rounded-2xl bg-gradient-to-br from-amber-50 to-yellow-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">Initial Cleared</p>
                <p className="text-2xl font-bold text-amber-700">{stats.initial_cleared}</p>
                <p className="text-xs text-amber-600">₹{incentive_structure.initial_cleared} each</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <UserCheck className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-lg rounded-2xl bg-gradient-to-br from-slate-50 to-gray-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">Pending</p>
                <p className="text-2xl font-bold text-slate-700">{stats.pending}</p>
                <p className="text-xs text-red-500">{stats.rejected} rejected</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-slate-500/10 flex items-center justify-center">
                <Clock className="h-5 w-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Earnings Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Total Earnings Card */}
        <Card className="border-0 shadow-lg rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <DollarSign className="h-5 w-5 text-emerald-500" />
              My Earnings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-emerald-500 to-green-600 rounded-xl text-white">
              <div>
                <p className="text-sm opacity-90">Total Earned</p>
                <p className="text-3xl font-bold">₹{earnings.total_earned.toLocaleString()}</p>
              </div>
              <DollarSign className="h-10 w-10 opacity-50" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-green-50 rounded-xl text-center">
                <p className="text-xs text-green-600 font-medium">Paid</p>
                <p className="text-xl font-bold text-green-700">₹{earnings.paid.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-xl text-center">
                <p className="text-xs text-amber-600 font-medium">Pending</p>
                <p className="text-xl font-bold text-amber-700">₹{earnings.pending.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Monthly Performance Card */}
        <Card className="border-0 shadow-lg rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="h-5 w-5 text-indigo-500" />
              {monthly.month}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-3 bg-indigo-50 rounded-xl">
                <p className="text-xs text-indigo-600">Referrals</p>
                <p className="text-2xl font-bold text-indigo-700">{monthly.referrals}</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-xl">
                <p className="text-xs text-purple-600">Placements</p>
                <p className="text-2xl font-bold text-purple-700">{monthly.placements}</p>
              </div>
              <div className="p-3 bg-pink-50 rounded-xl">
                <p className="text-xs text-pink-600">Earnings</p>
                <p className="text-lg font-bold text-pink-700">₹{monthly.earnings.toLocaleString()}</p>
              </div>
            </div>
            {monthly.bonus > 0 && (
              <div className="p-3 bg-gradient-to-r from-amber-100 to-yellow-100 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Gift className="h-5 w-5 text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">Bonus Earned!</span>
                </div>
                <span className="text-lg font-bold text-amber-700">+₹{monthly.bonus.toLocaleString()}</span>
              </div>
            )}
            {monthly.bonus === 0 && monthly.placements < 5 && (
              <div className="p-3 bg-slate-50 rounded-xl text-center">
                <p className="text-xs text-slate-500">
                  {5 - monthly.placements} more placements for ₹2,500 bonus
                </p>
                <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
                  <div 
                    className="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full transition-all"
                    style={{ width: `${(monthly.placements / 5) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Incentive Structure */}
      <Card className="border-0 shadow-lg rounded-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Gift className="h-5 w-5 text-pink-500" />
            Incentive Structure
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 rounded-xl">
              <h4 className="font-semibold text-blue-800 mb-2">Initial Round Cleared</h4>
              <p className="text-2xl font-bold text-blue-600">₹{incentive_structure.initial_cleared}</p>
              <p className="text-xs text-blue-600 mt-1">Per candidate who clears initial round</p>
            </div>
            <div className="p-4 bg-emerald-50 rounded-xl">
              <h4 className="font-semibold text-emerald-800 mb-2">Placement Incentives</h4>
              <div className="space-y-1 text-sm">
                {incentive_structure.tiers.map((tier, idx) => (
                  <div key={idx} className="flex justify-between text-emerald-700">
                    <span>₹{tier.min.toLocaleString()} - ₹{tier.max.toLocaleString()}</span>
                    <span className="font-semibold">₹{tier.incentive.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-4 p-4 bg-amber-50 rounded-xl">
            <h4 className="font-semibold text-amber-800 mb-2">Monthly Bonus Tiers</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {incentive_structure.bonus_tiers.map((tier, idx) => (
                <div key={idx} className="text-center p-2 bg-white/50 rounded-lg">
                  <p className="text-xs text-amber-700">{tier.placements} Placements</p>
                  <p className="font-bold text-amber-800">₹{tier.bonus.toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Recent Referrals */}
      <Card className="border-0 shadow-lg rounded-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <History className="h-5 w-5 text-violet-500" />
            Recent Referrals
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recent_referrals.length > 0 ? (
            <div className="space-y-3">
              {recent_referrals.map((ref, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white font-semibold">
                      {ref.candidate_name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{ref.candidate_name}</p>
                      <p className="text-xs text-slate-500">{ref.job_title} at {ref.company_name}</p>
                    </div>
                  </div>
                  <Badge className={
                    ref.status === 'Hired' ? 'bg-emerald-100 text-emerald-700' :
                    ref.status === 'Shortlisted' || ref.status === 'Interviewed' ? 'bg-blue-100 text-blue-700' :
                    ref.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                    'bg-slate-100 text-slate-700'
                  }>
                    {ref.status === 'Shortlisted' ? 'Initial Cleared' :
                     ref.status === 'Interviewed' ? 'Registered' :
                     ref.status === 'Hired' ? 'Placed' : ref.status}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-500 py-4">No referrals yet</p>
          )}
        </CardContent>
      </Card>
      
      {/* Payment History */}
      {payment_history && payment_history.length > 0 && (
        <Card className="border-0 shadow-lg rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="h-5 w-5 text-green-500" />
              Payment History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {payment_history.map((payment, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-xl">
                  <div>
                    <p className="font-medium text-slate-800">₹{payment.amount.toLocaleString()}</p>
                    <p className="text-xs text-slate-500">{payment.notes || payment.payment_type}</p>
                  </div>
                  <div className="text-right">
                    <Badge className="bg-green-100 text-green-700">{payment.status}</Badge>
                    <p className="text-xs text-slate-400 mt-1">
                      {payment.created_at ? formatISTDate(payment.created_at) : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const LeadSpecialist = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [availabilityStatus, setAvailabilityStatus] = useState('available');
  const [allUsers, setAllUsers] = useState([]);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [referralDialogOpen, setReferralDialogOpen] = useState(false);
  
  // Profile Dialog State
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('job-updates');
  const [profileData, setProfileData] = useState({
    name: '',
    profile_picture: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  const [referralData, setReferralData] = useState({
    contact_id: '',
    referred_to_company: '',
    position: '',
    notes: ''
  });
  // New candidate referral form state
  const [newCandidateDialogOpen, setNewCandidateDialogOpen] = useState(false);
  const [newCandidateForm, setNewCandidateForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    current_company: '',
    previous_company: '',
    country_state: '',
    linkedin_profile: '',
    resume_link: '',
    qualification: '',
    notice_period: '',
    experience: '',
    has_aadhar_card: 'No',
    has_pan_card: 'No',
    aadhar_linked_mobile: 'No',
    refer_to_company: '',
    position: '',
    cv_file_id: ''
  });
  const [cvFile, setCvFile] = useState(null);
  const [cvUploading, setCvUploading] = useState(false);
  const [myReferredCandidates, setMyReferredCandidates] = useState([]);
  const [timelineDialogOpen, setTimelineDialogOpen] = useState(false);
  const [selectedContactForTimeline, setSelectedContactForTimeline] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  
  // Candidate List State (Excel-like sheet)
  const [candidateList, setCandidateList] = useState([]);
  const [candidateListLoading, setCandidateListLoading] = useState(false);
  const [newCandidateRow, setNewCandidateRow] = useState({ name: '', email: '', phone: '', process: '', location: '', notes: '' });
  const [editingCandidateId, setEditingCandidateId] = useState(null);
  const [editingCandidateData, setEditingCandidateData] = useState({});
  
  // New state for Jobs and Companies
  const [jobUpdates, setJobUpdates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [myJobReferrals, setMyJobReferrals] = useState([]);
  const [jobReferralDialogOpen, setJobReferralDialogOpen] = useState(false);
  const [selectedJobForReferral, setSelectedJobForReferral] = useState(null);
  const [jobFilter, setJobFilter] = useState('all');
  const [jobSearchQuery, setJobSearchQuery] = useState('');
  const [referralStatusFilter, setReferralStatusFilter] = useState('all'); // New filter for My Refs
  const [selectedJobDetails, setSelectedJobDetails] = useState(null);
  const [jobDetailsDialogOpen, setJobDetailsDialogOpen] = useState(false);
  const [jobReferralForm, setJobReferralForm] = useState({
    candidate_name: '',
    candidate_email: '',
    candidate_phone: '',
    linkedin_url: '',
    experience: '',
    current_company: '',
    current_city_state: '',
    hear_about_us: '',
    notes: '',
    cv_file_id: ''
  });
  const [jobReferralCvFile, setJobReferralCvFile] = useState(null);
  const [jobReferralCvUploading, setJobReferralCvUploading] = useState(false);

  // Banner carousel state
  const [banners, setBanners] = useState([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);

  // Position Request state
  const [isPositionRequestOpen, setIsPositionRequestOpen] = useState(false);
  const [myPositionRequests, setMyPositionRequests] = useState([]);
  const [uploadingCV, setUploadingCV] = useState(false);
  const [positionRequestForm, setPositionRequestForm] = useState({
    candidate_name: '',
    company_name: '',
    position_name: '',
    company_link: '',
    cv_url: '',
    reason: '',
    notes: ''
  });

  // Messaging state
  const [isMessagingOpen, setIsMessagingOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [sentMessages, setSentMessages] = useState([]);
  const [messagingTab, setMessagingTab] = useState('inbox');
  const [showNewMessageView, setShowNewMessageView] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [isMessagesFullscreen, setIsMessagesFullscreen] = useState(false);
  const messagesEndRef = React.useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Handle browser back button - prevent going to login page
  useEffect(() => {
    // Push a state to history so back button doesn't leave the app
    window.history.pushState(null, '', window.location.href);
    
    const handlePopState = (event) => {
      // When back button is pressed, push state again to stay on page
      window.history.pushState(null, '', window.location.href);
      
      // If sidebar is open, close it instead
      if (isSidebarOpen) {
        setIsSidebarOpen(false);
        return;
      }
      
      // If any dialog is open, close it
      if (isMessagesFullscreen) {
        setIsMessagesFullscreen(false);
        return;
      }
      
      if (referralDialogOpen) {
        setReferralDialogOpen(false);
        return;
      }
      
      if (isProfileDialogOpen) {
        setIsProfileDialogOpen(false);
        return;
      }
      
      if (jobReferralDialogOpen) {
        setJobReferralDialogOpen(false);
        return;
      }
      
      if (jobDetailsDialogOpen) {
        setJobDetailsDialogOpen(false);
        return;
      }
      
      // Show a toast to indicate the app is still active
      toast.info('Press back again to stay in app', { duration: 1500 });
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isSidebarOpen, isMessagesFullscreen, referralDialogOpen, isProfileDialogOpen, jobReferralDialogOpen, jobDetailsDialogOpen]);

  useEffect(() => {
    filterContacts();
  }, [contacts, searchQuery]);

  // Update app badge when notifications change
  useEffect(() => {
    const unreadCount = notifications.filter(n => !n.read).length;
    updateAppBadge(unreadCount);
  }, [notifications]);

  // Real-time message count polling
  const prevUnreadCountRef = React.useRef(0);
  const isMessagesFullscreenRef = React.useRef(false);
  
  useEffect(() => {
    isMessagesFullscreenRef.current = isMessagesFullscreen;
  }, [isMessagesFullscreen]);
  
  useEffect(() => {
    const pollMessageCount = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const res = await axios.get(`${API}/messages/unread/count`, config);
        const newUnreadCount = res.data.unread_count || 0;
        
        // Show toast notification if unread count increased and messaging panel is not open
        if (newUnreadCount > prevUnreadCountRef.current && !isMessagesFullscreenRef.current) {
          toast('📩 New message received!', {
            description: 'Click Messages to view',
          });
        }
        
        prevUnreadCountRef.current = newUnreadCount;
        setUnreadMessageCount(newUnreadCount);
        
        // Also refresh conversations if fullscreen is open
        if (isMessagesFullscreenRef.current) {
          const convRes = await axios.get(`${API}/messages/conversations`, config);
          setConversations(convRes.data);
        }
      } catch (error) {
        console.error('Failed to poll message count:', error);
      }
    };
    
    // Poll every 3 seconds for real-time updates
    const messageInterval = setInterval(pollMessageCount, 3000);
    pollMessageCount(); // Initial poll immediately
    
    return () => clearInterval(messageInterval);
  }, []);

  // Auto-slide banners
  useEffect(() => {
    if (banners.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [banners.length]);

  // Subscribe to push notifications
  const subscribeToPushNotifications = async () => {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.log('Push notifications not supported');
        return;
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('Push notification permission denied');
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      
      // Get VAPID public key from server
      const vapidResponse = await axios.get(`${API}/push/vapid-public-key`);
      const vapidPublicKey = vapidResponse.data.vapid_public_key;
      
      if (!vapidPublicKey) {
        console.log('VAPID public key not available');
        return;
      }

      // Convert VAPID key to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
          .replace(/-/g, '+')
          .replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to server
      const token = getAuthToken();
      await axios.post(`${API}/push/subscribe`, subscription.toJSON(), {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Push notification subscription successful');
    } catch (error) {
      console.error('Push notification subscription failed:', error);
    }
  };

  const fetchData = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // First fetch user to check role
      const userRes = await axios.get(`${API}/auth/me`, config);
      setCurrentUser(userRes.data);
      
      // Redirect non-lead specialists to appropriate dashboard BEFORE fetching role-specific data
      if (userRes.data.role !== 'lead_specialist') {
        setLoading(false);
        if (userRes.data.role === 'candidate') {
          navigate('/candidate');
        } else {
          navigate('/');
        }
        return;
      }

      // Now fetch lead specialist specific data
      const [contactsRes, announcementsRes, referralsRes, notifRes, usersRes, unreadRes, referredCandidatesRes, jobsRes, companiesRes, myJobReferralsRes, bannersRes, positionReqsRes] = await Promise.all([
        axios.get(`${API}/lead-specialist/contacts`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/referral-announcements`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/referrals`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/notifications`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/users`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/messages/unread/count`, config).catch(() => ({ data: { unread_count: 0 } })),
        axios.get(`${API}/referred-candidates`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/job-updates`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/companies`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/lead-specialist/job-referrals/my`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/banners/public/lead_specialist`).catch(() => ({ data: [] })),
        axios.get(`${API}/position-requests/my`, config).catch(() => ({ data: [] }))
      ]);

      // Subscribe to push notifications
      subscribeToPushNotifications();
      
      setContacts(contactsRes.data);
      setAnnouncements(announcementsRes.data);
      setReferrals(referralsRes.data);
      setNotifications(notifRes.data);
      setBanners(bannersRes.data);
      setAvailabilityStatus(userRes.data.availability_status || 'available');
      setAllUsers(usersRes.data);
      setUnreadMessageCount(unreadRes.data.unread_count || 0);
      setMyReferredCandidates(referredCandidatesRes.data);
      setJobUpdates(jobsRes.data);
      setCompanies(companiesRes.data);
      setMyJobReferrals(myJobReferralsRes.data);
      setMyPositionRequests(positionReqsRes.data);
      
      // Pre-fill position request form with user name
      if (userRes.data.name) {
        setPositionRequestForm(prev => ({ ...prev, candidate_name: userRes.data.name }));
      }
    } catch (error) {
      if (error.response?.status === 401) {
        removeAuthToken();
        navigate('/login');
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterContacts = () => {
    let filtered = contacts;
    if (searchQuery) {
      filtered = filtered.filter(
        (c) =>
          c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.phone.includes(searchQuery) ||
          (c.email && c.email.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    setFilteredContacts(filtered);
  };

  const updateAvailabilityStatus = async (status) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/users/availability?status=${status}`, {}, config);
      setAvailabilityStatus(status);
      toast.success(`Status updated to ${status}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const availabilityOptions = [
    { value: 'available', label: 'Available', color: 'from-emerald-500 to-green-500', icon: '🟢' },
    { value: 'offline', label: 'Offline', color: 'from-gray-500 to-slate-600', icon: '⚫' },
    { value: 'meeting', label: 'Meeting', color: 'from-blue-500 to-indigo-600', icon: '🔵' },
    { value: 'training', label: 'Training', color: 'from-amber-500 to-orange-500', icon: '🟡' },
    { value: 'system', label: 'System', color: 'from-red-500 to-rose-600', icon: '🔴' }
  ];

  const currentAvailability = availabilityOptions.find(a => a.value === availabilityStatus) || availabilityOptions[0];

  // Candidate List Functions
  const fetchCandidateList = async () => {
    try {
      setCandidateListLoading(true);
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/lead-specialist/candidate-list`, config);
      setCandidateList(res.data);
    } catch (error) {
      console.error('Failed to fetch candidate list:', error);
    } finally {
      setCandidateListLoading(false);
    }
  };

  const addCandidateToList = async () => {
    if (!newCandidateRow.name.trim() || !newCandidateRow.email.trim()) {
      toast.error('Name and Email are required');
      return;
    }
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/lead-specialist/candidate-list`, newCandidateRow, config);
      toast.success('Candidate added successfully');
      setNewCandidateRow({ name: '', email: '', phone: '', process: '', location: '', notes: '' });
      fetchCandidateList();
    } catch (error) {
      toast.error('Failed to add candidate');
    }
  };

  const updateCandidateInList = async (candidateId) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/lead-specialist/candidate-list/${candidateId}`, editingCandidateData, config);
      toast.success('Candidate updated');
      setEditingCandidateId(null);
      setEditingCandidateData({});
      fetchCandidateList();
    } catch (error) {
      toast.error('Failed to update candidate');
    }
  };

  const deleteCandidateFromList = async (candidateId) => {
    if (!window.confirm('Are you sure you want to delete this candidate?')) return;
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/lead-specialist/candidate-list/${candidateId}`, config);
      toast.success('Candidate deleted');
      fetchCandidateList();
    } catch (error) {
      toast.error('Failed to delete candidate');
    }
  };

  const handleViewComments = async (contact) => {
    setSelectedContact(contact);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/contacts/${contact.id}/comments`, config);
      setComments(res.data);
    } catch (error) {
      toast.error('Failed to load comments');
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;

    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.post(
        `${API}/contacts/${selectedContact.id}/comments?comment=${encodeURIComponent(comment)}`,
        {},
        config
      );

      toast.success('Comment added!');
      setComment('');
      handleViewComments(selectedContact);
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleReferCandidate = async () => {
    if (!referralData.referred_to_company.trim()) {
      toast.error('Please enter company name');
      return;
    }

    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.post(`${API}/referrals`, referralData, config);
      toast.success('Candidate referred successfully!');
      setReferralDialogOpen(false);
      setReferralData({ contact_id: '', referred_to_company: '', position: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to refer candidate');
    }
  };

  const openReferralDialog = (contact) => {
    setReferralData({
      contact_id: contact.id,
      referred_to_company: contact.apply_to_company || '',
      position: '',
      notes: ''
    });
    setReferralDialogOpen(true);
  };

  const handleSubmitNewCandidate = async () => {
    if (!newCandidateForm.name.trim() || !newCandidateForm.email.trim() || !newCandidateForm.phone.trim()) {
      toast.error('Please fill in Name, Email and Phone');
      return;
    }
    if (!newCandidateForm.refer_to_company.trim() || !newCandidateForm.position.trim()) {
      toast.error('Please fill in Company Name and Position');
      return;
    }

    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      let cvFileId = newCandidateForm.cv_file_id;
      
      // Upload CV file if selected
      if (cvFile) {
        // Check file size (16MB limit)
        if (cvFile.size > 16 * 1024 * 1024) {
          toast.error(`File size exceeds 16MB limit. Your file is ${(cvFile.size / (1024*1024)).toFixed(2)}MB`);
          return;
        }
        
        setCvUploading(true);
        const formData = new FormData();
        formData.append('file', cvFile);
        
        try {
          const uploadRes = await axios.post(`${API}/cv/upload`, formData, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          cvFileId = uploadRes.data.file_id;
          toast.success('CV uploaded successfully!');
        } catch (uploadError) {
          toast.error(uploadError.response?.data?.detail || 'Failed to upload CV');
          setCvUploading(false);
          return;
        }
        setCvUploading(false);
      }
      
      // Submit the referral with CV file ID
      const submitData = { ...newCandidateForm, cv_file_id: cvFileId };
      await axios.post(`${API}/referred-candidates`, submitData, config);
      
      toast.success('Candidate referred successfully!');
      setNewCandidateDialogOpen(false);
      setCvFile(null);
      setNewCandidateForm({
        name: '',
        email: '',
        phone: '',
        address: '',
        current_company: '',
        previous_company: '',
        country_state: '',
        linkedin_profile: '',
        resume_link: '',
        qualification: '',
        notice_period: '',
        experience: '',
        has_aadhar_card: 'No',
        has_pan_card: 'No',
        aadhar_linked_mobile: 'No',
        refer_to_company: '',
        position: '',
        cv_file_id: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to submit candidate');
    }
  };

  const referralStatusColors = {
    'Pending': 'bg-yellow-500',
    'Accepted': 'bg-blue-500',
    'Rejected': 'bg-red-500',
    'Hired': 'bg-green-500',
    'Reviewing': 'bg-blue-500',
    'Shortlisted': 'bg-purple-500',
    'Interviewed': 'bg-indigo-500'
  };

  const announcementPriorityColors = {
    Urgent: 'from-red-500 to-red-600',
    High: 'from-orange-500 to-orange-600',
    Medium: 'from-purple-500 to-indigo-600',
    Low: 'from-gray-500 to-gray-600'
  };

  const statusColors = {
    'New Lead': 'bg-blue-500',
    'Contacted': 'bg-yellow-500',
    'Follow-up': 'bg-orange-500',
    'Converted': 'bg-green-500',
    'Lost': 'bg-gray-500',
    'Interviewed': 'bg-purple-500',
    'Hired': 'bg-emerald-600',
    'Rejected': 'bg-red-500'
  };

  // Job Referral handlers
  const openJobReferralDialog = (job) => {
    setSelectedJobForReferral(job);
    setJobReferralForm({
      candidate_name: '',
      candidate_email: '',
      candidate_phone: '',
      linkedin_url: '',
      experience: '',
      current_company: '',
      current_city_state: '',
      hear_about_us: '',
      notes: '',
      cv_file_id: ''
    });
    setJobReferralCvFile(null);
    setJobReferralDialogOpen(true);
  };

  const handleSubmitJobReferral = async () => {
    if (!jobReferralForm.candidate_name.trim() || !jobReferralForm.candidate_email.trim() || !jobReferralForm.candidate_phone.trim()) {
      toast.error('Please fill in Name, Email and Phone');
      return;
    }
    
    if (!jobReferralForm.current_city_state.trim()) {
      toast.error('Please fill in Current City/State');
      return;
    }
    
    if (!jobReferralForm.experience.trim()) {
      toast.error('Please fill in Total Experience');
      return;
    }

    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      let cvFileId = jobReferralForm.cv_file_id;
      
      // Upload CV file if selected
      if (jobReferralCvFile) {
        if (jobReferralCvFile.size > 16 * 1024 * 1024) {
          toast.error('File size exceeds 16MB limit');
          return;
        }
        
        setJobReferralCvUploading(true);
        const formData = new FormData();
        formData.append('file', jobReferralCvFile);
        
        try {
          const uploadRes = await axios.post(`${API}/cv/upload`, formData, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          cvFileId = uploadRes.data.file_id;
          toast.success('CV uploaded successfully!');
        } catch (uploadError) {
          toast.error(uploadError.response?.data?.detail || 'Failed to upload CV');
          setJobReferralCvUploading(false);
          return;
        }
        setJobReferralCvUploading(false);
      }
      
      // Submit the job referral
      const submitData = {
        job_id: selectedJobForReferral.id,
        candidate_name: jobReferralForm.candidate_name,
        candidate_email: jobReferralForm.candidate_email,
        candidate_phone: jobReferralForm.candidate_phone,
        linkedin_url: jobReferralForm.linkedin_url,
        experience: jobReferralForm.experience,
        current_company: jobReferralForm.current_company,
        current_city_state: jobReferralForm.current_city_state,
        hear_about_us: jobReferralForm.hear_about_us || 'Lead Specialist Referral',
        notes: jobReferralForm.notes,
        cv_file_id: cvFileId
      };
      
      await axios.post(`${API}/lead-specialist/job-referrals`, submitData, config);
      
      toast.success('Candidate referred successfully!');
      setJobReferralDialogOpen(false);
      setJobReferralCvFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit referral');
    }
  };

  const getWorkTypeIcon = (workType) => {
    switch(workType) {
      case 'Work from Home': return <Home className="h-4 w-4" />;
      case 'Work from Office': return <Building className="h-4 w-4" />;
      case 'Hybrid': return <Laptop className="h-4 w-4" />;
      default: return <Building className="h-4 w-4" />;
    }
  };

  const getWorkTypeBadgeColor = (workType) => {
    switch(workType) {
      case 'Work from Home': return 'bg-green-100 text-green-700';
      case 'Work from Office': return 'bg-blue-100 text-blue-700';
      case 'Hybrid': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const handleShareJob = (job, platform) => {
    const jobUrl = `${window.location.origin}/job/${job.id}`;
    const text = `Check out this job: ${job.position_name || 'Open Position'} at ${job.company_name}`;
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(jobUrl)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(jobUrl)}&text=${encodeURIComponent(text)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(jobUrl)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + jobUrl)}`
    };
    
    if (platform === 'copy') {
      const textArea = document.createElement('textarea');
      textArea.value = jobUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('Link copied to clipboard!');
      } catch (e) {
        toast.error('Could not copy link');
      }
      document.body.removeChild(textArea);
    } else {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const handleViewTimeline = async (contact) => {
    setSelectedContactForTimeline(contact);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/contacts/${contact.id}/status-history`, config);
      setStatusHistory(res.data);
      setTimelineDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load timeline');
    }
  };

  // Archive/Unarchive referral
  const handleArchiveReferral = async (referralId, archive) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API}/job-referrals/${referralId}/archive`, { is_archived: archive }, config);
      
      // Update local state
      setMyJobReferrals(prev => prev.map(r => 
        r.id === referralId ? { ...r, is_archived: archive } : r
      ));
      
      toast.success(archive ? 'Referral archived' : 'Referral unarchived');
    } catch (error) {
      toast.error('Failed to update referral');
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'New Lead': return <Clock className="h-4 w-4" />;
      case 'Contacted': return <Phone className="h-4 w-4" />;
      case 'Interviewed': return <Briefcase className="h-4 w-4" />;
      case 'Hired': return <CheckCircle2 className="h-4 w-4" />;
      case 'Rejected': return <XCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'New Lead': return 'bg-blue-500';
      case 'Contacted': return 'bg-yellow-500';
      case 'Interviewed': return 'bg-purple-500';
      case 'Hired': return 'bg-emerald-500';
      case 'Rejected': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  // Position Request Functions
  const handleCVUploadForPosition = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('CV must be less than 5MB');
      return;
    }
    
    setUploadingCV(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = getAuthToken();
      const response = await axios.post(`${API}/upload/cv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });
      
      setPositionRequestForm(prev => ({ ...prev, cv_url: response.data.url }));
      toast.success('CV uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload CV');
    } finally {
      setUploadingCV(false);
    }
  };

  const handleSubmitPositionRequest = async (e) => {
    e.preventDefault();
    
    if (!positionRequestForm.candidate_name || !positionRequestForm.company_name || 
        !positionRequestForm.position_name || !positionRequestForm.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API}/position-requests`, positionRequestForm, config);
      
      toast.success('Position request submitted! You will receive an update via email within a week.');
      setIsPositionRequestOpen(false);
      setPositionRequestForm({
        candidate_name: currentUser?.name || '',
        company_name: '',
        position_name: '',
        company_link: '',
        cv_url: '',
        reason: '',
        notes: ''
      });
      
      // Refresh position requests
      const response = await axios.get(`${API}/position-requests/my`, config);
      setMyPositionRequests(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const getPositionRequestStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'bg-yellow-100 text-yellow-700';
      case 'In Review': return 'bg-blue-100 text-blue-700';
      case 'Approved': return 'bg-green-100 text-green-700';
      case 'Rejected': return 'bg-red-100 text-red-700';
      case 'Completed': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Messaging functions
  const fetchConversations = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/messages/conversations`, config);
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    }
  };

  const fetchMessages = async (recipientEmail) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/messages/${recipientEmail}`, config);
      setConversationMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const handleSelectConversation = async (conv) => {
    // Set conversation immediately for instant UI response
    setSelectedConversation(conv);
    setConversationMessages([]); // Clear to show loading state
    
    // Fetch messages (non-blocking)
    fetchMessages(conv.other_user_email);
    
    // Mark messages as read in background
    try {
      const token = getAuthToken();
      axios.post(`${API}/messages/read/${conv.other_user_email}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(() => {
        setUnreadMessageCount(prev => Math.max(0, prev - (conv.unread_count || 0)));
      }).catch(() => {});
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    if (sendingMessage) return; // Prevent double-click
    
    const messageContent = newMessage.trim();
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const receiverEmail = selectedConversation.other_user_email || selectedConversation.email;
    
    // Clear input IMMEDIATELY for instant feedback
    setNewMessage('');
    setSendingMessage(true);
    
    const tempMessage = {
      id: tempId,
      sender_email: currentUser?.email,
      sender_name: currentUser?.name,
      receiver_email: receiverEmail,
      content: messageContent,
      timestamp: new Date().toISOString(),
      created_at: new Date().toISOString(),
      sending: true,
      status: 'sending'
    };
    
    // Optimistic update - show message immediately
    setConversationMessages(prev => [...prev, tempMessage]);
    
    // Scroll to bottom
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/messages`, {
        receiver_email: receiverEmail,
        content: messageContent
      }, config);
      
      // Mark as sent locally first (don't wait for fetch)
      setConversationMessages(prev => prev.map(m => 
        m.id === tempId ? { ...m, sending: false, status: 'sent' } : m
      ));
      
      // Refresh in background to sync with server
      setTimeout(async () => {
        try {
          const res = await axios.get(`${API}/messages/${receiverEmail}`, config);
          // Merge server messages, keeping optimistic ones that aren't duplicated
          setConversationMessages(prev => {
            const serverMessages = res.data;
            const pendingOptimistic = prev.filter(m => 
              m.id.startsWith('temp-') && 
              !serverMessages.some(sm => sm.content === m.content && sm.sender_email === m.sender_email)
            );
            return [...serverMessages, ...pendingOptimistic];
          });
        } catch (e) {}
      }, 500);
      
      fetchConversations(); // Update sidebar
    } catch (error) {
      // Mark as failed
      setConversationMessages(prev => prev.map(m => 
        m.id === tempId ? { ...m, sending: false, status: 'failed' } : m
      ));
      setNewMessage(messageContent); // Restore message
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  // Auto-scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  React.useEffect(() => {
    scrollToBottom();
  }, [conversationMessages]);

  // Auto-refresh messages every 10 seconds when dialog is open
  React.useEffect(() => {
    if (!isMessagingOpen || !selectedConversation) return;
    
    const interval = setInterval(() => {
      fetchMessages(selectedConversation.other_user_email);
    }, 10000);
    
    return () => clearInterval(interval);
  }, [isMessagingOpen, selectedConversation]);

  const fetchSentMessages = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/messages/sent/all`, config);
      setSentMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch sent messages:', error);
    }
  };

  // Fetch ALL users (not just CRM members) for messaging
  const fetchAllUsersForMessaging = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/messages/all-users`, config);
      // Filter out current user AND candidates (Lead Specialists can message Admin, User, CRM, and other Lead Specialists)
      const filteredUsers = response.data.filter(u => 
        u.email !== currentUser?.email && 
        u.role !== 'candidate'
      );
      setAllUsers(filteredUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      // Fallback: try to fetch from conversations endpoint
      try {
        const token = getAuthToken();
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const convRes = await axios.get(`${API}/messages/conversations`, config);
        // Extract unique users from conversations, excluding candidates
        const users = convRes.data
          .filter(c => c.other_user_role !== 'candidate')
          .map(c => ({
            email: c.other_user_email,
            name: c.other_user_name,
            role: c.other_user_role || 'user'
          }));
        setAllUsers(users);
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    }
  };

  const startNewConversation = (member) => {
    setSelectedConversation({
      other_user_email: member.email,
      other_user_name: member.name || member.email
    });
    setShowNewMessageView(false);
    setMessagingTab('inbox');
    setConversationMessages([]); // Clear immediately
    fetchMessages(member.email); // Fetch in background
  };

  // Open conversation for fullscreen messaging
  const openConversation = (member) => {
    setSelectedConversation({
      email: member.email,
      name: member.name || member.email,
      role: member.role,
      profile_image: member.profile_image
    });
    setConversationMessages([]);
    fetchMessages(member.email);
    
    // Mark messages as read
    const token = localStorage.getItem('token');
    if (token) {
      axios.put(`${API}/messages/read/${member.email}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(() => {
        // Update conversations to show 0 unread
        setConversations(prev => prev.map(c => 
          c.other_user_email === member.email ? { ...c, unread_count: 0 } : c
        ));
        // Refresh total unread count
        axios.get(`${API}/messages/unread/count`, { headers: { Authorization: `Bearer ${token}` } })
          .then(res => setUnreadMessageCount(res.data.unread_count || 0))
          .catch(() => {});
      }).catch(() => {});
    }
  };

  // Handle profile update
  const handleUpdateProfile = async () => {
    setSavingProfile(true);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API}/users/me/profile`, {
        name: profileData.name,
        profile_picture: profileData.profile_picture
      }, config);
      
      // Update local state
      setCurrentUser(prev => ({
        ...prev,
        name: profileData.name,
        profile_image: profileData.profile_picture
      }));
      
      toast.success('Profile updated successfully!');
      setIsProfileDialogOpen(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  // Open profile dialog with current data
  const openProfileDialog = () => {
    setProfileData({
      name: currentUser?.name || '',
      profile_picture: currentUser?.profile_image || ''
    });
    setIsProfileDialogOpen(true);
  };

  // Fetch conversations when messaging tab is active
  useEffect(() => {
    if (isMessagingOpen) {
      fetchConversations();
      fetchSentMessages();
      fetchAllUsersForMessaging();
    }
  }, [isMessagingOpen]);

  // Fetch data when fullscreen messages opens
  useEffect(() => {
    if (isMessagesFullscreen) {
      fetchConversations();
      fetchAllUsersForMessaging();
    }
  }, [isMessagesFullscreen]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#0F4C81] border-t-transparent mx-auto mb-4"></div>
          <p className="text-slate-500 text-sm font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-[#0F4C81] sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-2 md:px-6 py-3 md:py-3 flex items-center justify-between">
          {/* Hamburger Menu + Logo + App Name */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Hamburger Menu Button */}
            <button 
              onClick={() => setIsSidebarOpen(true)}
              className="h-10 w-10 flex items-center justify-center text-white hover:bg-white/10 rounded-lg transition-all"
              data-testid="hamburger-menu-btn"
            >
              <Menu className="h-6 w-6" strokeWidth={2} />
            </button>
            <div className="bg-white rounded-lg p-1.5 shadow-sm">
              <img src="/logo.png?v=15" alt="Officium Connect" className="h-6 md:h-7" data-testid="specialist-logo" />
            </div>
            <div>
              <span className="font-heading font-semibold text-sm md:text-lg tracking-tight whitespace-nowrap text-white">
                Officium Connect
              </span>
              <p className="text-[10px] text-white/60 hidden sm:block">Lead Specialist Portal</p>
            </div>
          </div>

          {/* Profile Button only */}
          <div className="flex items-center">
            <button 
              onClick={openProfileDialog}
              className="flex items-center gap-2 hover:bg-white/10 rounded-lg px-2 py-1.5 transition-all"
              data-testid="profile-btn"
            >
              {currentUser?.profile_image ? (
                <img 
                  src={currentUser.profile_image.startsWith('data:') ? currentUser.profile_image : `${API}/uploads/${currentUser.profile_image}`}
                  alt={currentUser?.name}
                  className="h-9 w-9 rounded-full object-cover border-2 border-white/50"
                />
              ) : (
                <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-sm">
                  {currentUser?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
              )}
              <span className="text-sm font-medium hidden sm:block text-white">{currentUser?.name}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Sidebar Drawer */}
      {isSidebarOpen && (
        <div className="fixed inset-0 z-[60]">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 transition-opacity"
            onClick={() => setIsSidebarOpen(false)}
          />
          {/* Sidebar */}
          <div className="absolute left-0 top-0 h-full w-72 bg-white shadow-2xl transform transition-transform animate-in slide-in-from-left duration-200">
            {/* Sidebar Header */}
            <div className="bg-[#0F4C81] p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white rounded-lg p-1">
                  <img src="/logo.png?v=15" alt="Officium Connect" className="h-6" />
                </div>
                <span className="font-heading font-semibold text-white text-sm">Menu</span>
              </div>
              <button 
                onClick={() => setIsSidebarOpen(false)}
                className="text-white hover:bg-white/10 p-2 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* User Info */}
            <div className="p-4 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center gap-3">
                {currentUser?.profile_image ? (
                  <img 
                    src={currentUser.profile_image.startsWith('data:') ? currentUser.profile_image : `${API}/uploads/${currentUser.profile_image}`}
                    alt={currentUser?.name}
                    className="h-10 w-10 rounded-full object-cover border-2 border-[#0F4C81]"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-[#0F4C81] flex items-center justify-center text-white font-bold">
                    {currentUser?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                )}
                <div>
                  <p className="font-semibold text-slate-900 text-sm">{currentUser?.name}</p>
                  <p className="text-xs text-slate-500">Lead Specialist</p>
                </div>
              </div>
            </div>
            
            {/* Navigation Items */}
            <nav className="p-2 space-y-1 overflow-y-auto max-h-[calc(100vh-200px)]">
              <button
                onClick={() => { setActiveTab('job-updates'); setIsSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'job-updates' ? 'bg-[#0F4C81] text-white' : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                <Briefcase className="h-5 w-5" />
                Jobs
                {jobUpdates.length > 0 && (
                  <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${activeTab === 'job-updates' ? 'bg-white/20' : 'bg-emerald-100 text-emerald-600'}`}>
                    {jobUpdates.length}
                  </span>
                )}
              </button>
              
              <button
                onClick={() => { setActiveTab('candidate-list'); setIsSidebarOpen(false); fetchCandidateList(); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'candidate-list' ? 'bg-[#0F4C81] text-white' : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                <FileText className="h-5 w-5" />
                My Candidate List
                {candidateList.length > 0 && (
                  <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${activeTab === 'candidate-list' ? 'bg-white/20' : 'bg-slate-100 text-slate-600'}`}>
                    {candidateList.length}
                  </span>
                )}
              </button>
              
              <button
                onClick={() => { setActiveTab('referrals'); setIsSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'referrals' ? 'bg-[#0F4C81] text-white' : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                <Send className="h-5 w-5" />
                My Referrals
                {myJobReferrals.length > 0 && (
                  <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${activeTab === 'referrals' ? 'bg-white/20' : 'bg-slate-100 text-slate-600'}`}>
                    {myJobReferrals.length}
                  </span>
                )}
              </button>
              
              <div className="pt-2 mt-2 border-t border-slate-200">
                <p className="px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">More Options</p>
              </div>
              
              <button
                onClick={() => { setActiveTab('candidates'); setIsSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'candidates' ? 'bg-[#0F4C81] text-white' : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                <UsersIcon className="h-5 w-5" />
                Assigned Candidates
              </button>
              
              <button
                onClick={() => { setActiveTab('bonuses'); setIsSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'bonuses' ? 'bg-[#0F4C81] text-white' : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                <Gift className="h-5 w-5" />
                Bonuses
              </button>
              
              <button
                onClick={() => { setActiveTab('request-position'); setIsSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'request-position' ? 'bg-[#0F4C81] text-white' : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                <FileText className="h-5 w-5" />
                Position Requests
                {myPositionRequests.filter(r => r.status === 'Pending').length > 0 && (
                  <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${activeTab === 'request-position' ? 'bg-white/20' : 'bg-amber-100 text-amber-600'}`}>
                    {myPositionRequests.filter(r => r.status === 'Pending').length}
                  </span>
                )}
              </button>
              
              <button
                onClick={() => { setActiveTab('messages'); setIsSidebarOpen(false); setIsMessagesFullscreen(true); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'messages' ? 'bg-[#0F4C81] text-white' : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                <MessageCircle className="h-5 w-5" />
                Messages
                {unreadMessageCount > 0 && (
                  <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${activeTab === 'messages' ? 'bg-white/20' : 'bg-red-100 text-red-600'}`}>
                    {unreadMessageCount}
                  </span>
                )}
              </button>
              
              <button
                onClick={() => { setActiveTab('my-performance'); setIsSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'my-performance' ? 'bg-[#0F4C81] text-white' : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                <TrendingUp className="h-5 w-5" />
                My Performance
              </button>
            </nav>
            
            {/* Bottom Actions */}
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-white">
              <button
                onClick={() => {
                  removeAuthToken();
                  navigate('/');
                  toast.success('Logged out successfully');
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-all"
              >
                <LogOut className="h-5 w-5" />
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-8">
        {/* Banner Carousel */}
        {banners.length > 0 && (
          <div className="relative w-full rounded-2xl overflow-hidden shadow-xl mb-6">
            {/* Banner Images */}
            <div className="relative aspect-[16/5] md:aspect-[16/4]">
              {banners.map((banner, index) => (
                <div
                  key={banner.id}
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    index === currentBannerIndex ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  {banner.link_url ? (
                    <a href={banner.link_url} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
                      <img
                        src={`${API}/banners/image/${banner.image_id}`}
                        alt={banner.title || `Banner ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </a>
                  ) : (
                    <img
                      src={`${API}/banners/image/${banner.image_id}`}
                      alt={banner.title || `Banner ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
              ))}
            </div>
            
            {/* Navigation Arrows */}
            {banners.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentBannerIndex((prev) => (prev - 1 + banners.length) % banners.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center transition-colors"
                >
                  <ChevronRight className="h-6 w-6 rotate-180" />
                </button>
                <button
                  onClick={() => setCurrentBannerIndex((prev) => (prev + 1) % banners.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center transition-colors"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}
            
            {/* Dots Indicator */}
            {banners.length > 1 && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                {banners.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentBannerIndex(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      index === currentBannerIndex 
                        ? 'bg-white w-6' 
                        : 'bg-white/50 hover:bg-white/70'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Announcements Banner */}
        {announcements.length > 0 && (
          <div className="mb-4 md:mb-6 space-y-2 md:space-y-3">
            {announcements.slice(0, 2).map((ann) => (
              <div 
                key={ann.id} 
                className={`bg-gradient-to-r ${announcementPriorityColors[ann.priority] || announcementPriorityColors.Medium} rounded-sm p-3 md:p-4 text-white`}
              >
                <div className="flex flex-wrap items-center gap-2 mb-1 md:mb-2">
                  <Gift className="h-4 w-4 md:h-5 md:w-5" />
                  <h3 className="font-heading font-semibold text-sm md:text-base">{ann.title}</h3>
                  <Badge className="bg-white/20 text-white text-[10px] md:text-xs">{ann.priority || 'Medium'}</Badge>
                  {ann.bonus_amount && (
                    <Badge className="bg-green-400 text-green-900 text-[10px] md:text-xs">{ann.bonus_amount}</Badge>
                  )}
                </div>
                <p className="text-xs md:text-sm text-white/90">{ann.description}</p>
                {ann.valid_until && (
                  <p className="text-[10px] md:text-xs text-white/70 mt-1">
                    Valid until: {formatISTDate(ann.valid_until)}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        <Tabs value={activeTab} className="space-y-4 md:space-y-6" onValueChange={(value) => {
          setActiveTab(value);
          if (value === 'messages') {
            setIsMessagesFullscreen(true);
          }
          if (value === 'candidate-list') {
            fetchCandidateList();
          }
        }}>
          <TabsList className="flex flex-wrap gap-2 bg-transparent p-0 h-auto justify-start">
            <TabsTrigger value="job-updates" data-testid="job-updates-tab" className="group relative flex items-center gap-2 px-4 py-2.5 text-sm bg-white border border-slate-200 shadow-card hover:shadow-card-hover data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white data-[state=active]:border-[#0F4C81] data-[state=active]:shadow-premium rounded-full transition-all duration-200">
              <Briefcase className="h-4 w-4" strokeWidth={1.5} />
              <span className="font-medium">Jobs</span>
              {jobUpdates.length > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] group-data-[state=active]:bg-white/20 group-data-[state=active]:text-white font-semibold">{jobUpdates.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="candidate-list" data-testid="candidate-list-tab" className="group flex items-center gap-2 px-4 py-2.5 text-sm bg-white border border-slate-200 shadow-card hover:shadow-card-hover data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white data-[state=active]:border-[#0F4C81] data-[state=active]:shadow-premium rounded-full transition-all duration-200">
              <FileText className="h-4 w-4" strokeWidth={1.5} />
              <span className="font-medium">My List</span>
              {candidateList.length > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 group-data-[state=active]:bg-white/20 group-data-[state=active]:text-white font-semibold">{candidateList.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="referrals" data-testid="referrals-tab" className="group relative flex items-center gap-2 px-4 py-2.5 text-sm bg-white border border-slate-200 shadow-card hover:shadow-card-hover data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white data-[state=active]:border-[#0F4C81] data-[state=active]:shadow-premium rounded-full transition-all duration-200">
              <Send className="h-4 w-4" strokeWidth={1.5} />
              <span className="font-medium">My Refs</span>
              {myJobReferrals.length > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 group-data-[state=active]:bg-white/20 group-data-[state=active]:text-white font-semibold">{myJobReferrals.length}</span>
              )}
            </TabsTrigger>
            
            {/* Hamburger Menu for more tabs */}
            <DropdownMenu open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-4 py-2.5 text-sm bg-white border border-slate-200 shadow-card hover:shadow-card-hover rounded-full transition-all duration-200">
                  <Menu className="h-4 w-4" strokeWidth={1.5} />
                  <span className="font-medium">More</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <TabsTrigger value="candidates" asChild>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                    <UsersIcon className="h-4 w-4 mr-2" />
                    Assigned
                  </DropdownMenuItem>
                </TabsTrigger>
                <TabsTrigger value="bonuses" asChild>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                    <Gift className="h-4 w-4 mr-2" />
                    Bonuses
                  </DropdownMenuItem>
                </TabsTrigger>
                <TabsTrigger value="request-position" asChild>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                    <FileText className="h-4 w-4 mr-2" />
                    Requests {myPositionRequests.filter(r => r.status === 'Pending').length > 0 && `(${myPositionRequests.filter(r => r.status === 'Pending').length})`}
                  </DropdownMenuItem>
                </TabsTrigger>
                <TabsTrigger value="messages" asChild>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Messages {unreadMessageCount > 0 && `(${unreadMessageCount})`}
                  </DropdownMenuItem>
                </TabsTrigger>
                <TabsTrigger value="my-performance" asChild>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                    <TrendingUp className="h-4 w-4 mr-2" />
                    Performance
                  </DropdownMenuItem>
                </TabsTrigger>
              </DropdownMenuContent>
            </DropdownMenu>
          </TabsList>


          {/* Job Updates Tab - Modern Dashboard */}
          <TabsContent value="job-updates" className="space-y-6">
            <JobsDashboard 
              jobs={jobUpdates}
              companies={companies}
              onViewJob={(job) => { setSelectedJobDetails(job); setJobDetailsDialogOpen(true); }}
              onReferJob={(job) => openJobReferralDialog(job)}
              onShareJob={(job, platform) => handleShareJob(job, platform)}
            />
          </TabsContent>

          {/* Candidate List Tab - Excel-like Sheet */}
          <TabsContent value="candidate-list" className="space-y-4">
            <div>
              <h2 className="font-heading text-xl md:text-3xl font-bold tracking-tight mb-1 bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent">
                My Candidate List
              </h2>
              <p className="text-sm text-muted-foreground">Manage your candidate database</p>
            </div>

            {/* Excel-like Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Name</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Email</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Phone</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Process</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Location</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Notes</th>
                      <th className="text-center px-4 py-3 font-semibold text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Add New Row */}
                    <tr className="border-b border-slate-100 bg-emerald-50/50">
                      <td className="px-2 py-2">
                        <Input
                          placeholder="Name *"
                          value={newCandidateRow.name}
                          onChange={(e) => setNewCandidateRow(prev => ({ ...prev, name: e.target.value }))}
                          className="h-9 text-sm border-slate-200"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <Input
                          type="email"
                          placeholder="Email *"
                          value={newCandidateRow.email}
                          onChange={(e) => setNewCandidateRow(prev => ({ ...prev, email: e.target.value }))}
                          className="h-9 text-sm border-slate-200"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <Input
                          type="tel"
                          placeholder="Phone"
                          value={newCandidateRow.phone}
                          onChange={(e) => setNewCandidateRow(prev => ({ ...prev, phone: e.target.value }))}
                          className="h-9 text-sm border-slate-200"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <Input
                          placeholder="Process"
                          value={newCandidateRow.process}
                          onChange={(e) => setNewCandidateRow(prev => ({ ...prev, process: e.target.value }))}
                          className="h-9 text-sm border-slate-200"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <Input
                          placeholder="Location"
                          value={newCandidateRow.location}
                          onChange={(e) => setNewCandidateRow(prev => ({ ...prev, location: e.target.value }))}
                          className="h-9 text-sm border-slate-200"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <Input
                          placeholder="Notes"
                          value={newCandidateRow.notes}
                          onChange={(e) => setNewCandidateRow(prev => ({ ...prev, notes: e.target.value }))}
                          className="h-9 text-sm border-slate-200"
                        />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <Button
                          size="sm"
                          onClick={addCandidateToList}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white h-9"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add
                        </Button>
                      </td>
                    </tr>

                    {/* Candidate Rows */}
                    {candidateListLoading ? (
                      <tr>
                        <td colSpan={7} className="text-center py-8 text-slate-500">
                          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                          Loading...
                        </td>
                      </tr>
                    ) : candidateList.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-8 text-slate-500">
                          No candidates in your list yet. Add your first candidate above.
                        </td>
                      </tr>
                    ) : (
                      candidateList.map((candidate) => (
                        <tr key={candidate.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                          {editingCandidateId === candidate.id ? (
                            <>
                              <td className="px-2 py-2">
                                <Input
                                  value={editingCandidateData.name || ''}
                                  onChange={(e) => setEditingCandidateData(prev => ({ ...prev, name: e.target.value }))}
                                  className="h-9 text-sm"
                                />
                              </td>
                              <td className="px-2 py-2">
                                <Input
                                  value={editingCandidateData.email || ''}
                                  onChange={(e) => setEditingCandidateData(prev => ({ ...prev, email: e.target.value }))}
                                  className="h-9 text-sm"
                                />
                              </td>
                              <td className="px-2 py-2">
                                <Input
                                  value={editingCandidateData.phone || ''}
                                  onChange={(e) => setEditingCandidateData(prev => ({ ...prev, phone: e.target.value }))}
                                  className="h-9 text-sm"
                                />
                              </td>
                              <td className="px-2 py-2">
                                <Input
                                  value={editingCandidateData.process || ''}
                                  onChange={(e) => setEditingCandidateData(prev => ({ ...prev, process: e.target.value }))}
                                  className="h-9 text-sm"
                                />
                              </td>
                              <td className="px-2 py-2">
                                <Input
                                  value={editingCandidateData.location || ''}
                                  onChange={(e) => setEditingCandidateData(prev => ({ ...prev, location: e.target.value }))}
                                  className="h-9 text-sm"
                                />
                              </td>
                              <td className="px-2 py-2">
                                <Input
                                  value={editingCandidateData.notes || ''}
                                  onChange={(e) => setEditingCandidateData(prev => ({ ...prev, notes: e.target.value }))}
                                  className="h-9 text-sm"
                                  placeholder="Add notes..."
                                />
                              </td>
                              <td className="px-2 py-2 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <Button size="sm" variant="ghost" onClick={() => updateCandidateInList(candidate.id)} className="h-8 w-8 p-0 text-emerald-600 hover:bg-emerald-100">
                                    <Check className="h-4 w-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => { setEditingCandidateId(null); setEditingCandidateData({}); }} className="h-8 w-8 p-0 text-slate-600 hover:bg-slate-100">
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-4 py-3 font-medium text-slate-900">{candidate.name}</td>
                              <td className="px-4 py-3 text-slate-600">{candidate.email}</td>
                              <td className="px-4 py-3 text-slate-600">
                                <div className="flex items-center gap-2">
                                  {candidate.phone || '-'}
                                  {candidate.phone && (
                                    <a
                                      href={`https://wa.me/${candidate.phone.replace(/[^0-9]/g, '')}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-green-600 hover:text-green-700"
                                      title="WhatsApp"
                                    >
                                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                      </svg>
                                    </a>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-slate-600">{candidate.process || '-'}</td>
                              <td className="px-4 py-3 text-slate-600">{candidate.location || '-'}</td>
                              <td className="px-4 py-3 text-slate-600 max-w-[150px]">
                                <span className="truncate block" title={candidate.notes}>{candidate.notes || '-'}</span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-center gap-1">
                                  <Button size="sm" variant="ghost" onClick={() => { setEditingCandidateId(candidate.id); setEditingCandidateData(candidate); }} className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-100">
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => deleteCandidateFromList(candidate.id)} className="h-8 w-8 p-0 text-red-600 hover:bg-red-100">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-xs text-slate-500 text-center">
              Total: {candidateList.length} candidates in your list
            </p>
          </TabsContent>


          <TabsContent value="candidates" className="space-y-4">
            <div>
              <h2 className="font-heading text-xl md:text-3xl font-bold tracking-tight mb-1 bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent">
                Assigned Candidates
              </h2>
              <p className="text-sm text-muted-foreground">Candidates assigned to you for follow-up</p>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 md:gap-4">
              <div className="relative flex-1 md:max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search candidates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 rounded-xl text-sm"
                />
              </div>
              <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
                <TrendingUp className="h-3 w-3 md:h-4 md:w-4" />
                <span>{referrals.filter(r => r.status === 'Hired').length} successful</span>
              </div>
            </div>

            <div className="grid gap-3 md:gap-4">
              {filteredContacts.length === 0 ? (
                <div className="border border-border rounded-sm p-8 md:p-12 text-center">
                  <p className="text-muted-foreground text-sm">
                    {searchQuery
                      ? 'No candidates found matching your search'
                      : 'No candidates assigned to you yet'}
                  </p>
                </div>
              ) : (
                filteredContacts.map((contact) => (
                  <div
                    key={contact.id}
                    className="border border-border rounded-sm bg-card p-3 md:p-6"
                    style={{
                      borderLeftWidth: '4px',
                      borderLeftColor:
                        contact.status === 'Converted'
                          ? '#22c55e'
                          : contact.status === 'Lost'
                          ? '#9ca3af'
                          : '#3b82f6'
                    }}
                  >
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-3 md:gap-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h3 className="font-heading text-sm md:text-lg font-semibold">{contact.name}</h3>
                          <Badge className={`${statusColors[contact.status]} text-white font-mono text-[10px] md:text-xs rounded-sm`}>
                            {contact.status}
                          </Badge>
                        </div>

                        <div className="space-y-2">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 md:gap-6 text-xs md:text-sm">
                            <div className="flex items-center gap-2">
                              <Phone className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" />
                              <a
                                href={`tel:${contact.phone}`}
                                className="font-mono text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {contact.phone}
                              </a>
                            </div>
                            {contact.email && (
                              <div className="flex items-center gap-2">
                                <Mail className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" />
                                <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline truncate max-w-[120px] md:max-w-none">
                                  {contact.email}
                                </a>
                              </div>
                            )}
                            {contact.company && (
                              <div className="flex items-center gap-2 hidden sm:flex">
                                <Building2 className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" />
                                <span>{contact.company}</span>
                              </div>
                            )}
                          </div>

                          <div className="flex flex-wrap items-center gap-1 md:gap-2 text-[10px] md:text-xs text-muted-foreground">
                            {contact.years_of_experience && (
                              <span className="bg-secondary px-1.5 md:px-2 py-0.5 md:py-1 rounded">
                                <strong>Exp:</strong> {contact.years_of_experience}y
                              </span>
                            )}
                            {contact.highest_qualification && (
                              <span className="bg-secondary px-1.5 md:px-2 py-0.5 md:py-1 rounded hidden sm:inline">
                                <strong>Qual:</strong> {contact.highest_qualification}
                              </span>
                            )}
                            {contact.skills && (
                              <span className="bg-secondary px-1.5 md:px-2 py-0.5 md:py-1 rounded hidden md:inline">
                                <strong>Skills:</strong> {contact.skills}
                              </span>
                            )}
                            {contact.apply_to_company && (
                              <span className="bg-blue-50 text-blue-700 px-1.5 md:px-2 py-0.5 md:py-1 rounded">
                                <strong>Apply:</strong> {contact.apply_to_company}
                              </span>
                            )}
                          </div>

                          {contact.notes && (
                            <p className="text-xs md:text-sm text-muted-foreground mt-2">{contact.notes}</p>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col gap-2 md:gap-3">
                        {/* Status Display (View Only) */}
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] md:text-xs text-muted-foreground font-medium">Status:</span>
                          <Badge className={`${statusColors[contact.status]} text-white font-mono text-[10px] md:text-xs rounded-sm px-2 md:px-3 py-0.5 md:py-1`}>
                            {contact.status}
                          </Badge>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            variant="default"
                            onClick={() => openReferralDialog(contact)}
                            className="rounded-sm bg-green-600 hover:bg-green-700 text-xs md:text-sm h-8 md:h-9 px-2 md:px-3"
                            data-testid={`refer-btn-${contact.id}`}
                          >
                            <Send className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                            Refer
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => handleViewTimeline(contact)}
                            className="rounded-sm text-xs md:text-sm h-8 md:h-9 px-2 md:px-3"
                            data-testid={`timeline-btn-${contact.id}`}
                          >
                            <History className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                            <span className="hidden sm:inline">Timeline</span>
                            <span className="sm:hidden">Hist</span>
                          </Button>
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                variant="outline"
                                onClick={() => handleViewComments(contact)}
                                className="rounded-sm text-xs md:text-sm h-8 md:h-9 px-2 md:px-3"
                              >
                                <MessageSquare className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                                <span className="hidden sm:inline">Comments</span>
                                <span className="sm:hidden">Note</span>
                              </Button>
                            </DialogTrigger>
                          <DialogContent className="w-[calc(100vw-2rem)] md:max-w-[600px] max-h-[80vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle className="font-heading">Comments - {contact.name}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 max-h-96 overflow-y-auto">
                              {comments.length === 0 ? (
                                <p className="text-sm text-muted-foreground text-center py-4">No comments yet</p>
                              ) : (
                                comments.map((c) => (
                                  <div key={c.id} className="border-b border-border pb-3">
                                    <div className="flex items-center justify-between mb-1">
                                      <p className="font-medium text-sm">{c.user_name}</p>
                                      <p className="text-xs text-muted-foreground font-mono">
                                        {formatIST(c.created_at)}
                                      </p>
                                    </div>
                                    <p className="text-sm">{c.comment}</p>
                                  </div>
                                ))
                              )}
                            </div>
                            <div className="space-y-2 pt-4 border-t">
                              <Textarea
                                placeholder="Add your comment..."
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                className="rounded-sm"
                                rows={3}
                              />
                              <Button onClick={handleAddComment} className="w-full rounded-sm">
                                Add Comment
                              </Button>
                            </div>
                          </DialogContent>
                          </Dialog>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* New Refer Candidate Tab */}
          <TabsContent value="refer" className="space-y-4">
            <Card className="rounded-lg shadow-lg border-0">
              <CardHeader className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="h-5 w-5" />
                  Refer a New Candidate
                </CardTitle>
                <CardDescription className="text-emerald-100">
                  Fill out this form to refer a candidate. The CRM team will be notified automatically.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Referral Details */}
                  <div className="space-y-4 md:col-span-2">
                    <h3 className="font-semibold text-sm text-muted-foreground border-b pb-2">Referral Details</h3>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="refer_to_company">Company Name (Where to Refer) <span className="text-red-500">*</span></Label>
                    <Input
                      id="refer_to_company"
                      placeholder="e.g., Google, TCS, Infosys"
                      value={newCandidateForm.refer_to_company}
                      onChange={(e) => setNewCandidateForm({...newCandidateForm, refer_to_company: e.target.value})}
                      className="rounded-sm"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="position">Position Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="position"
                      placeholder="e.g., Software Engineer, Data Analyst"
                      value={newCandidateForm.position}
                      onChange={(e) => setNewCandidateForm({...newCandidateForm, position: e.target.value})}
                      className="rounded-sm"
                    />
                  </div>

                  {/* Basic Details */}
                  <div className="space-y-4 md:col-span-2 mt-4">
                    <h3 className="font-semibold text-sm text-muted-foreground border-b pb-2">Basic Details</h3>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="name">Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="name"
                      placeholder="Candidate full name"
                      value={newCandidateForm.name}
                      onChange={(e) => setNewCandidateForm({...newCandidateForm, name: e.target.value})}
                      className="rounded-sm"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email ID <span className="text-red-500">*</span></Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="candidate@email.com"
                      value={newCandidateForm.email}
                      onChange={(e) => setNewCandidateForm({...newCandidateForm, email: e.target.value})}
                      className="rounded-sm"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Contact Number <span className="text-red-500">*</span></Label>
                    <Input
                      id="phone"
                      placeholder="+91 9876543210"
                      value={newCandidateForm.phone}
                      onChange={(e) => setNewCandidateForm({...newCandidateForm, phone: e.target.value})}
                      className="rounded-sm"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      placeholder="Full address"
                      value={newCandidateForm.address}
                      onChange={(e) => setNewCandidateForm({...newCandidateForm, address: e.target.value})}
                      className="rounded-sm"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="country_state">Country/State</Label>
                    <Input
                      id="country_state"
                      placeholder="e.g., Maharashtra, India"
                      value={newCandidateForm.country_state}
                      onChange={(e) => setNewCandidateForm({...newCandidateForm, country_state: e.target.value})}
                      className="rounded-sm"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="linkedin">LinkedIn Profile</Label>
                    <Input
                      id="linkedin"
                      placeholder="https://linkedin.com/in/..."
                      value={newCandidateForm.linkedin_profile}
                      onChange={(e) => setNewCandidateForm({...newCandidateForm, linkedin_profile: e.target.value})}
                      className="rounded-sm"
                    />
                  </div>

                  {/* Professional Details */}
                  <div className="space-y-4 md:col-span-2 mt-4">
                    <h3 className="font-semibold text-sm text-muted-foreground border-b pb-2">Professional Details</h3>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="qualification">Qualification</Label>
                    <Input
                      id="qualification"
                      placeholder="e.g., B.Tech, MBA"
                      value={newCandidateForm.qualification}
                      onChange={(e) => setNewCandidateForm({...newCandidateForm, qualification: e.target.value})}
                      className="rounded-sm"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="experience">Total Experience</Label>
                    <Input
                      id="experience"
                      placeholder="e.g., 3 years"
                      value={newCandidateForm.experience}
                      onChange={(e) => setNewCandidateForm({...newCandidateForm, experience: e.target.value})}
                      className="rounded-sm"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="current_company">Current Company (if any)</Label>
                    <Input
                      id="current_company"
                      placeholder="Current employer"
                      value={newCandidateForm.current_company}
                      onChange={(e) => setNewCandidateForm({...newCandidateForm, current_company: e.target.value})}
                      className="rounded-sm"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="previous_company">Previous Company (if any)</Label>
                    <Input
                      id="previous_company"
                      placeholder="Previous employer"
                      value={newCandidateForm.previous_company}
                      onChange={(e) => setNewCandidateForm({...newCandidateForm, previous_company: e.target.value})}
                      className="rounded-sm"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="notice_period">Notice Period</Label>
                    <Input
                      id="notice_period"
                      placeholder="e.g., 30 days, Immediate"
                      value={newCandidateForm.notice_period}
                      onChange={(e) => setNewCandidateForm({...newCandidateForm, notice_period: e.target.value})}
                      className="rounded-sm"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="resume">Resume Link (Optional)</Label>
                    <Input
                      id="resume"
                      placeholder="Google Drive / Dropbox link"
                      value={newCandidateForm.resume_link}
                      onChange={(e) => setNewCandidateForm({...newCandidateForm, resume_link: e.target.value})}
                      className="rounded-sm"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Upload CV (Max 16MB)</Label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-emerald-400 transition-colors">
                      {cvFile ? (
                        <div className="flex items-center justify-between bg-emerald-50 p-2 rounded">
                          <div className="flex items-center gap-2">
                            <FileText className="h-5 w-5 text-emerald-600" />
                            <div className="text-left">
                              <p className="text-sm font-medium truncate max-w-[200px]">{cvFile.name}</p>
                              <p className="text-xs text-muted-foreground">{(cvFile.size / (1024*1024)).toFixed(2)} MB</p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setCvFile(null)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                if (file.size > 16 * 1024 * 1024) {
                                  toast.error(`File too large (${(file.size / (1024*1024)).toFixed(2)}MB). Max 16MB allowed.`);
                                  return;
                                }
                                setCvFile(file);
                              }
                            }}
                            className="hidden"
                          />
                          <div className="flex flex-col items-center gap-2">
                            <Upload className="h-8 w-8 text-gray-400" />
                            <span className="text-sm text-muted-foreground">Click to upload CV</span>
                            <span className="text-xs text-gray-400">PDF, DOC, DOCX (Max 16MB)</span>
                            <span className="text-xs text-amber-600">⚠️ Auto-deleted after 30 days</span>
                          </div>
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Document Details */}
                  <div className="space-y-4 md:col-span-2 mt-4">
                    <h3 className="font-semibold text-sm text-muted-foreground border-b pb-2">Document Details</h3>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Aadhar Card</Label>
                    <Select
                      value={newCandidateForm.has_aadhar_card}
                      onValueChange={(value) => setNewCandidateForm({...newCandidateForm, has_aadhar_card: value})}
                    >
                      <SelectTrigger className="rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>PAN Card</Label>
                    <Select
                      value={newCandidateForm.has_pan_card}
                      onValueChange={(value) => setNewCandidateForm({...newCandidateForm, has_pan_card: value})}
                    >
                      <SelectTrigger className="rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Aadhar linked with Mobile</Label>
                    <Select
                      value={newCandidateForm.aadhar_linked_mobile}
                      onValueChange={(value) => setNewCandidateForm({...newCandidateForm, aadhar_linked_mobile: value})}
                    >
                      <SelectTrigger className="rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <Button
                    onClick={handleSubmitNewCandidate}
                    disabled={cvUploading}
                    className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 rounded-lg shadow-lg"
                    data-testid="submit-referral-btn"
                  >
                    {cvUploading ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Uploading CV...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Submit Referral
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* My Referred Candidates List */}
            {myReferredCandidates.length > 0 && (
              <Card className="rounded-lg">
                <CardHeader>
                  <CardTitle className="text-lg">My Recent Referrals</CardTitle>
                  <CardDescription>Candidates you've referred ({myReferredCandidates.length})</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {myReferredCandidates.slice(0, 5).map(candidate => (
                      <div key={candidate.id} className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                        <div>
                          <p className="font-medium">{candidate.name}</p>
                          <p className="text-xs text-muted-foreground">{candidate.phone} • {candidate.experience || 'N/A'}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={candidate.eligible === 'Yes' ? 'default' : candidate.eligible === 'No' ? 'destructive' : 'secondary'}>
                            {candidate.eligible}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {candidate.calling_status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="referrals" className="space-y-4">
            <div>
              <h2 className="font-heading text-xl md:text-3xl font-bold tracking-tight mb-1 bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
                My Referrals
              </h2>
              <p className="text-sm text-muted-foreground">Track all your candidate referrals</p>
            </div>
            
            {/* Job Referrals Stats */}
            <div className="grid gap-4 md:grid-cols-4 mb-6">
              <Card className="rounded-xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Job Referrals</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-heading font-bold">{myJobReferrals.length}</div>
                </CardContent>
              </Card>
              <Card className="rounded-xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-yellow-600">Pending</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-heading font-bold text-yellow-600">
                    {myJobReferrals.filter(r => r.status === 'Pending').length}
                  </div>
                </CardContent>
              </Card>
              <Card className="rounded-xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-purple-600">Shortlisted</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-heading font-bold text-purple-600">
                    {myJobReferrals.filter(r => r.status === 'Shortlisted').length}
                  </div>
                </CardContent>
              </Card>
              <Card className="rounded-xl border-green-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-green-600">Hired</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-heading font-bold text-green-600">
                    {myJobReferrals.filter(r => r.status === 'Hired').length}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Status Filters for Referrals */}
            <div className="overflow-x-auto -mx-4 px-4">
              <div className="flex gap-2 min-w-max pb-2">
                {['all', 'Pending', 'Shortlisted', 'Hired', 'Rejected', 'Archived'].map((status) => {
                  const count = status === 'all' 
                    ? myJobReferrals.filter(r => !r.is_archived).length
                    : status === 'Archived'
                      ? myJobReferrals.filter(r => r.is_archived).length
                      : myJobReferrals.filter(r => r.status === status && !r.is_archived).length;
                  return (
                    <button
                      key={status}
                      onClick={() => setReferralStatusFilter(status)}
                      className={`px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                        referralStatusFilter === status
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-white text-gray-600 hover:bg-gray-100 border'
                      }`}
                    >
                      {status === 'all' ? 'All' : status} ({count})
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Job Referrals List */}
            {(() => {
              const filteredReferrals = myJobReferrals.filter(r => {
                if (referralStatusFilter === 'all') return !r.is_archived;
                if (referralStatusFilter === 'Archived') return r.is_archived;
                return r.status === referralStatusFilter && !r.is_archived;
              });
              
              return filteredReferrals.length === 0 ? (
                <div className="border border-border rounded-xl p-12 text-center bg-white">
                  <Send className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {referralStatusFilter === 'all' 
                      ? 'No job referrals yet. Go to Jobs tab and start referring candidates!'
                      : referralStatusFilter === 'Archived'
                        ? 'No archived referrals'
                        : `No ${referralStatusFilter.toLowerCase()} referrals`}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredReferrals.map((referral) => (
                    <Card key={referral.id} className={`overflow-hidden ${referral.is_archived ? 'opacity-75 bg-gray-50' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold">{referral.candidate_name}</h3>
                              <Badge className={referralStatusColors[referral.status] || 'bg-gray-500'}>{referral.status}</Badge>
                              {referral.is_archived && <Badge variant="outline" className="text-xs">Archived</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {referral.job_title} at {referral.company_name}
                            </p>
                            <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {referral.candidate_phone}
                              </span>
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {referral.candidate_email}
                              </span>
                              {referral.experience && (
                                <span>Exp: {referral.experience}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleArchiveReferral(referral.id, !referral.is_archived)}
                              className={`p-2 rounded-lg transition-colors ${
                                referral.is_archived 
                                  ? 'text-blue-500 hover:bg-blue-50' 
                                  : 'text-amber-500 hover:bg-amber-50'
                              }`}
                              title={referral.is_archived ? 'Unarchive' : 'Archive'}
                            >
                              {referral.is_archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                            </button>
                            <span className="text-xs text-muted-foreground">
                              {formatISTDate(referral.created_at)}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              );
            })()}

            {/* Legacy Referrals */}
            {referrals.length > 0 && (
              <div className="mt-8">
                <h3 className="font-semibold mb-4 text-muted-foreground">Previous Referrals</h3>
                <div className="grid gap-4 md:grid-cols-4 mb-6">
              <Card className="rounded-xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Referrals</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-heading font-bold">{referrals.length}</div>
                </CardContent>
              </Card>
              <Card className="rounded-xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-yellow-600">Pending</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-heading font-bold text-yellow-600">
                    {referrals.filter(r => r.status === 'Pending').length}
                  </div>
                </CardContent>
              </Card>
              <Card className="rounded-xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-blue-600">Accepted</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-heading font-bold text-blue-600">
                    {referrals.filter(r => r.status === 'Accepted').length}
                  </div>
                </CardContent>
              </Card>
              <Card className="rounded-xl border-green-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-green-600">Hired</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-heading font-bold text-green-600">
                    {referrals.filter(r => r.status === 'Hired').length}
                  </div>
                </CardContent>
              </Card>
            </div>
              <div className="space-y-3">
                {referrals.map((referral) => (
                  <div
                    key={referral.id}
                    className="border border-border rounded-xl bg-card p-4"
                    style={{
                      borderLeftWidth: '4px',
                      borderLeftColor:
                        referral.status === 'Hired' ? '#22c55e' :
                        referral.status === 'Accepted' ? '#3b82f6' :
                        referral.status === 'Rejected' ? '#ef4444' : '#eab308'
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h4 className="font-medium">{referral.contact_name}</h4>
                          <Badge className={`${referralStatusColors[referral.status]} text-white text-xs`}>
                            {referral.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Referred to <span className="font-medium">{referral.referred_to_company}</span>
                          {referral.position && ` for ${referral.position}`}
                        </p>
                        <p className="text-xs text-muted-foreground font-mono mt-1">
                          {formatISTFull(referral.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="bonuses" className="space-y-4">
            <div>
              <h2 className="font-heading text-xl md:text-3xl font-bold tracking-tight mb-1 bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">
                Bonus Programs
              </h2>
              <p className="text-sm text-muted-foreground">Earn rewards for successful referrals</p>
            </div>
            {announcements.length === 0 ? (
              <div className="border border-border rounded-xl p-12 text-center bg-white">
                <Gift className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No active bonus programs at the moment. Check back later!</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {announcements.map((ann) => (
                  <Card key={ann.id} className="rounded-sm border-purple-200">
                    <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50">
                      <div className="flex items-center justify-between">
                        <CardTitle className="font-heading text-lg">{ann.title}</CardTitle>
                        {ann.bonus_amount && (
                          <Badge className="bg-green-500 text-white text-lg px-3 py-1">
                            {ann.bonus_amount}
                          </Badge>
                        )}
                      </div>
                      {ann.valid_until && (
                        <CardDescription>
                          Valid until {formatIST(ann.valid_until, 'MMMM dd, yyyy')}
                        </CardDescription>
                      )}
                    </CardHeader>
                    <CardContent className="pt-4">
                      <p className="text-sm text-muted-foreground">{ann.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Request Position Tab */}
          <TabsContent value="request-position" className="space-y-4">
            <div>
              <h2 className="font-heading text-xl md:text-3xl font-bold tracking-tight mb-1 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                Request for a Position
              </h2>
              <p className="text-sm text-muted-foreground">Request an update about a position or a new company listing</p>
            </div>

            {/* Request Form */}
            <Card className="border shadow-md">
              <CardContent className="p-6">
                <form onSubmit={handleSubmitPositionRequest} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="req_candidate_name">Your Name *</Label>
                      <Input
                        id="req_candidate_name"
                        value={positionRequestForm.candidate_name}
                        onChange={(e) => setPositionRequestForm(prev => ({ ...prev, candidate_name: e.target.value }))}
                        placeholder="Enter your full name"
                        required
                        data-testid="req-candidate-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="req_company_name">Company Name *</Label>
                      <Input
                        id="req_company_name"
                        value={positionRequestForm.company_name}
                        onChange={(e) => setPositionRequestForm(prev => ({ ...prev, company_name: e.target.value }))}
                        placeholder="e.g., Google, Amazon"
                        required
                        data-testid="req-company-name"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="req_position_name">Position Name *</Label>
                      <Input
                        id="req_position_name"
                        value={positionRequestForm.position_name}
                        onChange={(e) => setPositionRequestForm(prev => ({ ...prev, position_name: e.target.value }))}
                        placeholder="e.g., Software Engineer"
                        required
                        data-testid="req-position-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="req_company_link">Company / Job Posting Link</Label>
                      <Input
                        id="req_company_link"
                        type="url"
                        value={positionRequestForm.company_link}
                        onChange={(e) => setPositionRequestForm(prev => ({ ...prev, company_link: e.target.value }))}
                        placeholder="https://..."
                        data-testid="req-company-link"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="req_cv">Upload CV (PDF, DOC - Max 5MB)</Label>
                    <div className="flex items-center gap-3">
                      <Input
                        id="req_cv"
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleCVUploadForPosition}
                        className="flex-1"
                        disabled={uploadingCV}
                        data-testid="req-cv-upload"
                      />
                      {uploadingCV && <span className="text-sm text-muted-foreground">Uploading...</span>}
                      {positionRequestForm.cv_url && (
                        <Badge variant="outline" className="text-green-600 border-green-600">
                          <CheckCircle2 className="h-3 w-3 mr-1" /> CV Uploaded
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="req_reason">Reason for Request *</Label>
                    <Select 
                      value={positionRequestForm.reason} 
                      onValueChange={(value) => setPositionRequestForm(prev => ({ ...prev, reason: value }))}
                    >
                      <SelectTrigger data-testid="req-reason-select">
                        <SelectValue placeholder="Select a reason" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="New Position Request">New Position Request</SelectItem>
                        <SelectItem value="Update on Existing Position">Update on Existing Position</SelectItem>
                        <SelectItem value="Request for New Company">Request for New Company</SelectItem>
                        <SelectItem value="Job Not Listed">Job Not Listed on Platform</SelectItem>
                        <SelectItem value="Career Guidance">Career Guidance</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="req_notes">Additional Notes</Label>
                    <Textarea
                      id="req_notes"
                      value={positionRequestForm.notes}
                      onChange={(e) => setPositionRequestForm(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Any additional details about your request..."
                      rows={3}
                      data-testid="req-notes"
                    />
                  </div>

                  {/* Info Notice */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> Our team will review your request and you will receive an update via email within <strong>one week</strong>.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
                    data-testid="submit-request-btn"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Submit Request
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* My Previous Requests */}
            {myPositionRequests.length > 0 && (
              <Card className="border shadow-md">
                <CardHeader>
                  <CardTitle className="text-lg">My Previous Requests</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {myPositionRequests.map(req => (
                      <div key={req.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                        <div>
                          <p className="font-medium">{req.position_name} at {req.company_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatIST(req.created_at, 'MMM d, yyyy')} • {req.reason}
                          </p>
                          {req.admin_notes && (
                            <p className="text-xs text-amber-700 mt-1 bg-amber-50 p-1 rounded">Admin: {req.admin_notes}</p>
                          )}
                        </div>
                        <Badge className={getPositionRequestStatusColor(req.status)}>
                          {req.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Messages Tab - WhatsApp style inline messaging */}
          <TabsContent value="messages" className="space-y-4">
            <div>
              <h2 className="font-heading text-xl md:text-3xl font-bold tracking-tight mb-1 bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">
                Messages
              </h2>
              <p className="text-sm text-muted-foreground">Chat with team members, admins and users</p>
            </div>

            {/* Messages Container - WhatsApp style */}
            <div className="bg-white rounded-2xl shadow-lg border overflow-hidden">
              {/* Header */}
              <div className="bg-[#075E54] p-3 md:p-4 flex items-center gap-3">
                <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-white/20 flex items-center justify-center">
                  <MessageCircle className="h-4 w-4 md:h-5 md:w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-base md:text-lg font-bold text-white">Team Chat</h3>
                  <p className="text-[10px] md:text-xs text-white/70">{allUsers.length} staff members</p>
                </div>
              </div>
              
              <div className="flex h-[500px] md:h-[600px] bg-[#ECE5DD]">
                {/* Sidebar - All Users List */}
                <div className={`w-full md:w-80 bg-white border-r flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
                  <div className="p-2 md:p-3 border-b bg-[#F0F2F5]">
                    {/* Search Bar */}
                    <div className="relative mb-2">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                      <input 
                        type="text"
                        value={userSearchQuery}
                        onChange={(e) => setUserSearchQuery(e.target.value)}
                        placeholder="Search users..." 
                        className="w-full pl-9 pr-4 py-2 text-sm rounded-full border border-gray-200 bg-white focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366] outline-none transition-all"
                      />
                    </div>
                    <div className="flex gap-1 text-xs">
                      <span className="px-2 py-1 rounded-full bg-[#25D366] text-white">
                        Staff ({allUsers.length})
                      </span>
                    </div>
                  </div>
                  <ScrollArea className="flex-1">
                    {allUsers.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        <p>No users available</p>
                        <p className="text-xs mt-1">Loading users...</p>
                      </div>
                    ) : (
                      <div className="space-y-1 p-2">
                        <p className="text-xs font-semibold text-[#075E54] px-3 py-2 uppercase tracking-wider">
                          Click to start chat
                        </p>
                        {allUsers
                          .filter(member => 
                            !userSearchQuery || 
                            member.name?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                            member.email?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                            member.role?.toLowerCase().includes(userSearchQuery.toLowerCase())
                          )
                          .map((member) => {
                          const existingConv = conversations.find(c => 
                            c.other_user_email === member.email || c.partner_email === member.email
                          );
                          const unreadCount = existingConv?.unread_count || 0;
                          const roleColor = {
                            admin: 'from-red-500 to-pink-500',
                            crm_user: 'from-blue-500 to-indigo-500',
                            lead_specialist: 'from-amber-500 to-orange-500',
                            telesales: 'from-purple-500 to-violet-500',
                            employer: 'from-emerald-500 to-teal-500',
                            candidate: 'from-gray-400 to-gray-500'
                          }[member.role] || 'from-[#25D366] to-[#128C7E]';
                          
                          return (
                            <div
                              key={member.email}
                              onClick={() => existingConv ? handleSelectConversation(existingConv) : startNewConversation(member)}
                              className={`p-3 rounded-xl cursor-pointer hover:bg-[#F0F2F5] transition-colors ${selectedConversation?.other_user_email === member.email ? 'bg-[#F0F2F5]' : ''}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`h-10 w-10 rounded-full bg-gradient-to-br ${roleColor} flex items-center justify-center text-white text-sm font-bold`}>
                                  {(member.name || member.email).charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium text-sm truncate text-slate-800">{member.name || member.email}</p>
                                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                                      member.role === 'admin' ? 'bg-red-100 text-red-700' :
                                      member.role === 'crm_user' ? 'bg-blue-100 text-blue-700' :
                                      member.role === 'lead_specialist' ? 'bg-amber-100 text-amber-700' :
                                      member.role === 'telesales' ? 'bg-purple-100 text-purple-700' :
                                      member.role === 'employer' ? 'bg-emerald-100 text-emerald-700' :
                                      'bg-gray-100 text-gray-600'
                                    }`}>
                                      {member.role === 'crm_user' ? 'CRM' : member.role?.replace('_', ' ') || 'User'}
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-500 truncate">
                                    {existingConv?.last_message || member.email}
                                  </p>
                                </div>
                                {existingConv?.unread_count > 0 && (
                                  <Badge className="bg-[#25D366] text-white text-[10px] h-5 w-5 flex items-center justify-center rounded-full p-0">
                                    {existingConv.unread_count}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </ScrollArea>
                </div>

                {/* Messages Area */}
                <div className={`flex-1 flex flex-col ${selectedConversation ? 'flex' : 'hidden md:flex'}`}>
                  {!selectedConversation ? (
                    /* Welcome View */
                    <div className="flex-1 flex items-center justify-center bg-[#ECE5DD]">
                      <div className="text-center p-8">
                        <div className="w-24 h-24 rounded-full bg-[#25D366]/10 flex items-center justify-center mx-auto mb-4">
                          <MessageCircle className="h-12 w-12 text-[#25D366]" />
                        </div>
                        <h3 className="text-xl font-semibold text-[#075E54]">Team Chat</h3>
                        <p className="text-gray-500 mt-2 max-w-sm mx-auto text-sm">
                          Select a team member from the list to start a conversation about referrals and opportunities.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Conversation Header */}
                      <div className="p-3 border-b bg-[#F0F2F5] flex items-center gap-3">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="md:hidden rounded-full p-2"
                          onClick={() => setSelectedConversation(null)}
                        >
                          <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#25D366] to-[#128C7E] flex items-center justify-center text-white font-bold">
                          {selectedConversation?.other_user_name?.charAt(0) || selectedConversation?.other_user_email?.charAt(0) || '?'}
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-slate-800">{selectedConversation?.other_user_name || selectedConversation?.other_user_email}</p>
                          <p className="text-xs text-gray-500">CRM Team Member</p>
                        </div>
                      </div>
                      
                      {/* Messages List */}
                      <ScrollArea className="flex-1 p-4" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23e5ddd5\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}>
                        <div className="space-y-3">
                          {conversationMessages.map((msg, idx) => {
                            const isMe = msg.sender_email === currentUser?.email;
                            const messageTime = msg.created_at || msg.timestamp;
                            return (
                              <div key={idx} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[75%] rounded-lg px-3 py-2 shadow-sm ${isMe ? 'bg-[#DCF8C6] text-slate-800' : 'bg-white text-slate-800'}`}>
                                  <SafeHtmlRenderer html={msg.content} className="text-sm" />
                                  <p className={`text-[10px] mt-1 text-right ${isMe ? 'text-gray-600' : 'text-gray-400'}`}>
                                    {messageTime ? formatISTTime(messageTime) : '...'}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </ScrollArea>
                      
                      {/* Message Input */}
                      <div className="p-3 border-t bg-[#F0F2F5]">
                        <div className="flex gap-2 items-end">
                          <div className="flex-1 bg-white rounded-2xl border shadow-sm">
                            <RichTextEditor
                              value={newMessage}
                              onChange={setNewMessage}
                              placeholder="Type a message..."
                              className="min-h-[44px] max-h-[120px] overflow-y-auto border-0 focus:ring-0"
                            />
                          </div>
                          <Button 
                            onClick={handleSendMessage}
                            disabled={sendingMessage || !newMessage.trim()}
                            className="h-11 w-11 rounded-full bg-[#25D366] hover:bg-[#128C7E] p-0 flex items-center justify-center shadow-lg"
                          >
                            {sendingMessage ? (
                              <Loader2 className="h-5 w-5 animate-spin text-white" />
                            ) : (
                              <Send className="h-5 w-5 text-white" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* My Performance Tab */}
          <TabsContent value="my-performance" className="space-y-6">
            <MyPerformanceDashboard />
          </TabsContent>
        </Tabs>

        {/* Referral Dialog */}
        <Dialog open={referralDialogOpen} onOpenChange={setReferralDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="font-heading">Refer Candidate</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company">Company Name *</Label>
                <Input
                  id="company"
                  placeholder="Enter company name"
                  value={referralData.referred_to_company}
                  onChange={(e) => setReferralData({ ...referralData, referred_to_company: e.target.value })}
                  className="rounded-sm"
                  data-testid="referral-company-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="position">Position/Role</Label>
                <Input
                  id="position"
                  placeholder="e.g., Software Developer"
                  value={referralData.position}
                  onChange={(e) => setReferralData({ ...referralData, position: e.target.value })}
                  className="rounded-sm"
                  data-testid="referral-position-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Any additional notes..."
                  value={referralData.notes}
                  onChange={(e) => setReferralData({ ...referralData, notes: e.target.value })}
                  className="rounded-sm"
                  rows={3}
                  data-testid="referral-notes-input"
                />
              </div>
              <Button 
                onClick={handleReferCandidate} 
                className="w-full rounded-sm bg-green-600 hover:bg-green-700"
                data-testid="submit-referral-btn"
              >
                <Send className="h-4 w-4 mr-2" />
                Submit Referral
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Candidate Progress Timeline Dialog */}
        <Dialog open={timelineDialogOpen} onOpenChange={setTimelineDialogOpen}>
          <DialogContent className="sm:max-w-[550px]">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <History className="h-5 w-5" />
                Progress Timeline - {selectedContactForTimeline?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Current Status */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
                <div className={`w-10 h-10 rounded-full ${getStatusColor(selectedContactForTimeline?.status)} flex items-center justify-center text-white`}>
                  {getStatusIcon(selectedContactForTimeline?.status)}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Current Status</p>
                  <p className="font-semibold">{selectedContactForTimeline?.status}</p>
                </div>
              </div>

              {/* Timeline */}
              <div className="relative">
                <h4 className="text-sm font-medium mb-4 text-muted-foreground">Status History</h4>
                {statusHistory.length === 0 ? (
                  <div className="text-center py-8 border border-dashed rounded-lg">
                    <History className="h-10 w-10 text-muted-foreground mx-auto mb-2 opacity-50" />
                    <p className="text-sm text-muted-foreground">No status changes recorded yet</p>
                    <p className="text-xs text-muted-foreground mt-1">Changes will appear here when you update the status</p>
                  </div>
                ) : (
                  <div className="space-y-0">
                    {statusHistory.map((entry, index) => (
                      <div key={entry.id} className="relative flex gap-4 pb-6">
                        {/* Timeline Line */}
                        {index !== statusHistory.length - 1 && (
                          <div className="absolute left-5 top-10 w-0.5 h-full bg-border"></div>
                        )}
                        
                        {/* Status Icon */}
                        <div className={`w-10 h-10 rounded-full ${getStatusColor(entry.new_status)} flex items-center justify-center text-white shrink-0 z-10`}>
                          {getStatusIcon(entry.new_status)}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 bg-white border rounded-lg p-3 shadow-sm">
                          <div className="flex items-center gap-2 mb-1">
                            {entry.old_status && (
                              <>
                                <Badge variant="outline" className="text-xs bg-slate-50">
                                  {entry.old_status}
                                </Badge>
                                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                              </>
                            )}
                            <Badge className={`${getStatusColor(entry.new_status)} text-white text-xs`}>
                              {entry.new_status}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Changed by <span className="font-medium">{entry.changed_by_name}</span>
                          </p>
                          <p className="text-xs text-muted-foreground font-mono mt-1">
                            {formatISTFull(entry.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Job Referral Dialog */}
        <Dialog open={jobReferralDialogOpen} onOpenChange={setJobReferralDialogOpen}>
          <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-amber-500" />
                Refer Candidate for {selectedJobForReferral?.position_name || 'Position'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {selectedJobForReferral && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm font-medium">{selectedJobForReferral.company_name}</p>
                  <p className="text-xs text-muted-foreground">{selectedJobForReferral.position_name}</p>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Candidate Name <span className="text-red-500">*</span></Label>
                  <Input
                    placeholder="Full name"
                    value={jobReferralForm.candidate_name}
                    onChange={(e) => setJobReferralForm({...jobReferralForm, candidate_name: e.target.value})}
                    className="rounded-lg"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email <span className="text-red-500">*</span></Label>
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={jobReferralForm.candidate_email}
                    onChange={(e) => setJobReferralForm({...jobReferralForm, candidate_email: e.target.value})}
                    className="rounded-lg"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone <span className="text-red-500">*</span></Label>
                  <Input
                    placeholder="+91 9876543210"
                    value={jobReferralForm.candidate_phone}
                    onChange={(e) => setJobReferralForm({...jobReferralForm, candidate_phone: e.target.value})}
                    className="rounded-lg"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Total Experience *</Label>
                  <Input
                    placeholder="e.g., 3 years"
                    value={jobReferralForm.experience}
                    onChange={(e) => setJobReferralForm({...jobReferralForm, experience: e.target.value})}
                    className="rounded-lg"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Current City/State *</Label>
                  <Input
                    placeholder="e.g., Mumbai, Maharashtra"
                    value={jobReferralForm.current_city_state}
                    onChange={(e) => setJobReferralForm({...jobReferralForm, current_city_state: e.target.value})}
                    className="rounded-lg"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Current Company</Label>
                  <Input
                    placeholder="Company name"
                    value={jobReferralForm.current_company}
                    onChange={(e) => setJobReferralForm({...jobReferralForm, current_company: e.target.value})}
                    className="rounded-lg"
                  />
                </div>
                <div className="space-y-2">
                  <Label>LinkedIn URL</Label>
                  <Input
                    placeholder="https://linkedin.com/in/..."
                    value={jobReferralForm.linkedin_url}
                    onChange={(e) => setJobReferralForm({...jobReferralForm, linkedin_url: e.target.value})}
                    className="rounded-lg"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Upload CV</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => setJobReferralCvFile(e.target.files[0])}
                      className="rounded-lg"
                    />
                    {jobReferralCvFile && (
                      <Button variant="ghost" size="icon" onClick={() => setJobReferralCvFile(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {jobReferralCvFile && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <FileText className="h-3 w-3" />
                      {jobReferralCvFile.name}
                    </p>
                  )}
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Notes</Label>
                  <Textarea
                    placeholder="Any additional notes about the candidate..."
                    value={jobReferralForm.notes}
                    onChange={(e) => setJobReferralForm({...jobReferralForm, notes: e.target.value})}
                    className="rounded-lg"
                    rows={3}
                  />
                </div>
              </div>
              
              <Button 
                onClick={handleSubmitJobReferral}
                disabled={jobReferralCvUploading}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 rounded-lg"
              >
                {jobReferralCvUploading ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Uploading CV...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Referral
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Job Details Dialog */}
        <Dialog open={jobDetailsDialogOpen} onOpenChange={setJobDetailsDialogOpen}>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-amber-500" />
                Job Details
              </DialogTitle>
            </DialogHeader>
            {selectedJobDetails && (
              <div className="space-y-4">
                {/* Company Info */}
                <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl">
                  {selectedJobDetails.company_logo_url ? (
                    <img 
                      src={`${API.replace('/api', '')}${selectedJobDetails.company_logo_url}`}
                      alt={selectedJobDetails.company_name}
                      className="h-16 w-16 rounded-xl object-cover border"
                    />
                  ) : (
                    <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-2xl">
                      {selectedJobDetails.company_name?.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-lg">{selectedJobDetails.company_name}</h3>
                    <p className="text-amber-700 font-medium">{selectedJobDetails.position_name || 'Open Position'}</p>
                  </div>
                </div>

                {/* Key Details */}
                <div className="grid grid-cols-2 gap-3">
                  {selectedJobDetails.location && (
                    <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                      <MapPin className="h-4 w-4 text-slate-500" />
                      <div>
                        <p className="text-xs text-muted-foreground">Location</p>
                        <p className="text-sm font-medium">{selectedJobDetails.location}</p>
                      </div>
                    </div>
                  )}
                  {selectedJobDetails.work_type && (
                    <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                      {selectedJobDetails.work_type === 'Work from Home' ? <Home className="h-4 w-4 text-green-500" /> :
                       selectedJobDetails.work_type === 'Hybrid' ? <Laptop className="h-4 w-4 text-purple-500" /> :
                       <Building className="h-4 w-4 text-blue-500" />}
                      <div>
                        <p className="text-xs text-muted-foreground">Work Type</p>
                        <p className="text-sm font-medium">{selectedJobDetails.work_type}</p>
                      </div>
                    </div>
                  )}
                  {selectedJobDetails.salary_range && (
                    <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                      <DollarSign className="h-4 w-4 text-green-500" />
                      <div>
                        <p className="text-xs text-muted-foreground">Salary</p>
                        <p className="text-sm font-medium">{selectedJobDetails.salary_currency || '₹'} {selectedJobDetails.salary_range}</p>
                      </div>
                    </div>
                  )}
                  {selectedJobDetails.experience && (
                    <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                      <Clock className="h-4 w-4 text-blue-500" />
                      <div>
                        <p className="text-xs text-muted-foreground">Experience</p>
                        <p className="text-sm font-medium">{selectedJobDetails.experience}</p>
                      </div>
                    </div>
                  )}
                  {selectedJobDetails.education && (
                    <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg col-span-2">
                      <GraduationCap className="h-4 w-4 text-amber-500" />
                      <div>
                        <p className="text-xs text-muted-foreground">Education</p>
                        <p className="text-sm font-medium">{selectedJobDetails.education}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Job Description */}
                {selectedJobDetails.job_description && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm">Job Description</h4>
                    <Linkify componentDecorator={(decoratedHref, decoratedText, key) => (
                      <a href={decoratedHref} key={key} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        {decoratedText}
                      </a>
                    )}>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedJobDetails.job_description}</p>
                    </Linkify>
                  </div>
                )}

                {/* Requirements */}
                {selectedJobDetails.requirements && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm">Requirements</h4>
                    <Linkify componentDecorator={(decoratedHref, decoratedText, key) => (
                      <a href={decoratedHref} key={key} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        {decoratedText}
                      </a>
                    )}>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedJobDetails.requirements}</p>
                    </Linkify>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button 
                    className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
                    onClick={() => { setJobDetailsDialogOpen(false); openJobReferralDialog(selectedJobDetails); }}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Refer Candidate
                  </Button>
                  <Button variant="outline" onClick={() => handleShareJob(selectedJobDetails, 'copy')}>
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        
        {/* Profile Dialog */}
        <Dialog open={isProfileDialogOpen} onOpenChange={setIsProfileDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-teal-500" />
                Edit Profile
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              {/* Profile Photo */}
              <div className="flex flex-col items-center gap-3">
                <ProfilePhotoUpload
                  currentPhoto={profileData.profile_picture}
                  onPhotoChange={(photo) => setProfileData(prev => ({ ...prev, profile_picture: photo }))}
                  name={profileData.name}
                  size="lg"
                />
                <p className="text-sm text-gray-500">Click to change photo</p>
              </div>

              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="profile-name">Display Name</Label>
                <Input
                  id="profile-name"
                  value={profileData.name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter your name"
                  className="focus-visible:ring-teal-500"
                />
              </div>

              {/* Email (Read-only) */}
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  value={currentUser?.email || ''}
                  disabled
                  className="bg-gray-50"
                />
                <p className="text-xs text-gray-500">Email cannot be changed</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsProfileDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleUpdateProfile} 
                disabled={savingProfile}
                className="bg-teal-500 hover:bg-teal-600"
              >
                {savingProfile ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Fullscreen Messaging Overlay */}
        {isMessagesFullscreen && (
          <div className="fixed inset-0 z-50 bg-gray-100" data-testid="messages-fullscreen">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-600 to-emerald-600 p-3 md:p-4 shadow-lg">
              <div className="max-w-7xl mx-auto flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button 
                    onClick={() => setIsMessagesFullscreen(false)}
                    className="h-10 w-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all"
                  >
                    <X className="h-5 w-5 text-white" />
                  </button>
                  <div>
                    <h2 className="text-lg md:text-xl font-bold text-white">Messages</h2>
                    <p className="text-xs text-white/70">{allUsers.length} staff members</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {unreadMessageCount > 0 && (
                    <span className="px-2 py-1 bg-green-500 text-white text-xs rounded-full font-bold">
                      {unreadMessageCount} unread
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Main Content */}
            <div className="flex h-[calc(100vh-64px)] bg-[#ECE5DD]">
              {/* Sidebar - Users List */}
              <div className={`w-full md:w-80 bg-white border-r flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
                <div className="p-3 border-b bg-[#F0F2F5]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <input 
                      type="text"
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      placeholder="Search users..." 
                      className="w-full pl-9 pr-4 py-2.5 text-sm rounded-full border border-gray-200 bg-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-all"
                    />
                  </div>
                </div>
                <ScrollArea className="flex-1">
                  <div className="space-y-1 p-2">
                    {allUsers
                      .filter(member => 
                        !userSearchQuery || 
                        member.name?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                        member.email?.toLowerCase().includes(userSearchQuery.toLowerCase())
                      )
                      .map((member) => {
                        const existingConv = conversations.find(c => 
                          c.other_user_email === member.email || c.partner_email === member.email
                        );
                        const unreadCount = existingConv?.unread_count || 0;
                        
                        return (
                          <div
                            key={member.id}
                            onClick={() => openConversation(member)}
                            className={`p-3 rounded-xl cursor-pointer transition-all ${
                              selectedConversation?.email === member.email 
                                ? 'bg-teal-100 border-l-4 border-teal-500' 
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="relative">
                                {member.profile_image ? (
                                  <img 
                                    src={member.profile_image.startsWith('data:') ? member.profile_image : `${API}/uploads/${member.profile_image}`}
                                    alt={member.name}
                                    className="h-12 w-12 rounded-full object-cover border-2 border-gray-100"
                                  />
                                ) : (
                                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-white font-bold text-lg">
                                    {member.name?.charAt(0)?.toUpperCase() || 'U'}
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-800 truncate">{member.name}</p>
                                <p className="text-xs text-gray-500 capitalize">{member.role?.replace(/_/g, ' ')}</p>
                              </div>
                              {unreadCount > 0 && (
                                <div className="flex-shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-teal-500 text-white text-xs font-bold flex items-center justify-center animate-pulse">
                                  {unreadCount > 99 ? '99+' : unreadCount}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </ScrollArea>
              </div>
              
              {/* Chat Area */}
              <div className={`flex-1 flex flex-col ${selectedConversation ? 'flex' : 'hidden md:flex'}`}>
                {selectedConversation ? (
                  <>
                    {/* Chat Header */}
                    <div className="bg-[#075E54] p-3 flex items-center gap-3 shadow">
                      <button
                        onClick={() => setSelectedConversation(null)}
                        className="md:hidden h-8 w-8 rounded-full bg-white/20 flex items-center justify-center"
                      >
                        <ArrowLeft className="h-4 w-4 text-white" />
                      </button>
                      {selectedConversation.profile_image ? (
                        <img 
                          src={selectedConversation.profile_image.startsWith('data:') ? selectedConversation.profile_image : `${API}/uploads/${selectedConversation.profile_image}`}
                          alt={selectedConversation.name}
                          className="h-10 w-10 rounded-full object-cover border-2 border-white/30"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center text-white font-bold">
                          {selectedConversation.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-semibold text-white">{selectedConversation.name}</p>
                        <p className="text-xs text-white/70 capitalize">{selectedConversation.role?.replace(/_/g, ' ')}</p>
                      </div>
                    </div>
                    
                    {/* Messages */}
                    <ScrollArea className="flex-1 p-3" style={{backgroundImage: "url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')"}}>
                      <div className="space-y-2 max-w-3xl mx-auto">
                        {conversationMessages.length === 0 ? (
                          <div className="text-center py-10">
                            <div className="h-16 w-16 mx-auto rounded-full bg-white shadow-lg flex items-center justify-center mb-3">
                              <MessageCircle className="h-8 w-8 text-teal-500" />
                            </div>
                            <p className="text-gray-600 font-medium">No messages yet</p>
                            <p className="text-sm text-gray-400">Start the conversation!</p>
                          </div>
                        ) : (
                          conversationMessages.map((msg, index) => {
                            const isOwn = msg.sender_email === currentUser?.email;
                            return (
                              <div key={msg.id || index} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[80%] p-3 rounded-2xl shadow ${
                                  isOwn 
                                    ? 'bg-[#DCF8C6] rounded-tr-none' 
                                    : 'bg-white rounded-tl-none'
                                }`}>
                                  <p className="text-sm whitespace-pre-wrap break-words" dangerouslySetInnerHTML={{ __html: msg.content }} />
                                  <p className={`text-[10px] mt-1 ${isOwn ? 'text-gray-500 text-right' : 'text-gray-400'}`}>
                                    {new Date(msg.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                    {isOwn && msg.read && <span className="ml-1 text-blue-500">✓✓</span>}
                                  </p>
                                </div>
                              </div>
                            );
                          })
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                    </ScrollArea>
                    
                    {/* Input */}
                    <div className="bg-[#F0F2F5] p-3 border-t">
                      <div className="flex items-center gap-2 max-w-3xl mx-auto">
                        <input
                          type="text"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                          placeholder="Type a message..."
                          className="flex-1 px-4 py-3 rounded-full border border-gray-200 bg-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none text-sm"
                        />
                        <button
                          onClick={handleSendMessage}
                          disabled={!newMessage.trim() || sendingMessage}
                          className="h-12 w-12 rounded-full bg-teal-500 hover:bg-teal-600 text-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg"
                        >
                          {sendingMessage ? (
                            <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          ) : (
                            <Send className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                    <div className="text-center p-8">
                      <div className="h-24 w-24 mx-auto rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center mb-4 shadow-xl">
                        <MessageCircle className="h-12 w-12 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">Team Messages</h3>
                      <p className="text-gray-500">Select a conversation to start chatting</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default LeadSpecialist;
